import { Controller } from '@hotwired/stimulus'

const ENGINE_FILTERS = [
  'contrast(1.35) saturate(1.35)',
  'contrast(1.6) saturate(.8) sepia(.2)',
  'contrast(1.5) saturate(1.7) hue-rotate(8deg)',
  'contrast(1.8) saturate(.55) brightness(1.08)',
  'contrast(1.45) saturate(1.25) sepia(.3)',
  'contrast(1.7) saturate(1.5) hue-rotate(-12deg)'
]

const IMAGE_FILTERS = ['none', 'contrast(1.7)', 'hue-rotate(35deg) contrast(1.4)', 'contrast(2) grayscale(.6)', 'sepia(.75) contrast(1.25)', 'brightness(1.45) contrast(1.6)', 'saturate(2.2) contrast(1.4)', 'grayscale(.8) contrast(1.5)', 'saturate(1.8) hue-rotate(10deg)', 'contrast(1.8) opacity(.82)', 'contrast(1.45) hue-rotate(170deg)', 'sepia(.3) brightness(1.3)', 'sepia(.65) hue-rotate(55deg)', 'sepia(.4) saturate(1.4)', 'grayscale(.75) brightness(1.2)', 'sepia(.7) hue-rotate(160deg)', 'saturate(2) hue-rotate(300deg)', 'grayscale(.9) contrast(1.8)', 'saturate(1.5) hue-rotate(90deg)', 'grayscale(1) contrast(1.5)']

const TEMPLATE_SLOTS = {
  1: { x: 10, y: 10, width: 80, height: 80 }, 2: { x: 10, y: 10, width: 80, height: 80 }, 3: { x: 12, y: 12, width: 76, height: 76 }, 4: { x: 20, y: 25, width: 60, height: 38 }, 5: { x: 14, y: 18, width: 72, height: 58 }, 6: { x: 8, y: 10, width: 84, height: 78 }, 7: { x: 12, y: 44, width: 76, height: 40 }, 8: { x: 8, y: 8, width: 84, height: 84 }, 9: { x: 9, y: 16, width: 82, height: 70 }, 10: { x: 18, y: 10, width: 64, height: 76 }, 11: { x: 7, y: 24, width: 86, height: 44 }, 12: { x: 18, y: 20, width: 64, height: 54 }, 13: { x: 10, y: 20, width: 80, height: 60 }, 14: { x: 8, y: 8, width: 84, height: 84 }, 15: { x: 8, y: 10, width: 84, height: 72 }, 16: { x: 12, y: 12, width: 76, height: 66 }, 17: { x: 12, y: 18, width: 76, height: 56 }, 18: { x: 22, y: 22, width: 56, height: 56 }, 19: { x: 10, y: 12, width: 80, height: 72 }, 20: { x: 12, y: 20, width: 76, height: 60 }
}

const ELEMENT_PRESETS = {
  line: ['28%', '44px'], arrow: ['30%', '44px'], 'arrow-dot': ['30%', '44px'], circle: ['18%', '18%'], square: ['18%', '18%'], triangle: ['18%', '16%'], frame: ['70%', '70%'], 'frame-cyan': ['70%', '70%'], 'frame-pink': ['70%', '70%'], 'frame-yellow': ['70%', '70%']
}

const RESIZE_HANDLE_POSITIONS = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']
const MAX_PAGES = 10
const DEFAULT_CANVAS_SIZE = 900
const DEFAULT_CANVAS_BACKGROUND = '#25313c'
const STICKER_POINTS = [[50, 0], [62, 19], [82, 8], [84, 30], [100, 38], [84, 52], [97, 72], [74, 75], [67, 100], [49, 84], [29, 98], [25, 77], [1, 72], [16, 53], [0, 38], [20, 30], [18, 8], [39, 19]]

const DESIGN_FRAME_STYLES = {
  'design-1': { color: '#24cfe3', width: 30 }, 'design-2': { color: '#1d8dd8', width: 30, double: true }, 'design-3': { color: '#7ffcf2', width: 24, dash: [28, 18] }, 'design-4': { color: '#f05267', width: 32 }, 'design-5': { color: '#b453c7', width: 30, double: true }, 'design-6': { color: '#ef5273', width: 24, dash: [24, 16] }, 'design-7': { color: '#ffd92f', width: 32 }, 'design-8': { color: '#ef4265', width: 30, double: true }, 'design-9': { color: '#24cfe3', width: 26, dash: [22, 14] }, 'design-10': { color: '#111111', width: 32 }
}

export default class extends Controller {
  static targets = ['pane', 'canvas', 'drawingCanvas', 'canvasWrap', 'backgroundLayer', 'overlayLayer', 'emptyState', 'fileInput', 'engineSettings', 'engineName', 'pageStrip', 'toast', 'saveButton', 'objectToolbar', 'objectLabel', 'opacityInput', 'zoomLabel', 'layersPanel', 'layersList', 'editPanel', 'mediaPlayButton', 'projectData', 'addPageButton', 'deletePageButton', 'backgroundInput', 'removeTemplateButton', 'removeEngineButton']

  connect () {
    this.ctx = this.canvasTarget.getContext('2d')
    this.visibleDrawingCtx = this.drawingCanvasTarget.getContext('2d')
    this.pages = [this.defaultPage()]
    this.pageIndex = 0
    this.history = []
    this.brushSize = 3
    this.brushColor = '#111111'
    this.drawCompositeOperation = 'source-over'
    this.isDrawing = false
    this.zoom = 1
    this.keydownHandler = event => this.handleKeydown(event)
    document.addEventListener('keydown', this.keydownHandler)
    this.gallerySelectionHandler = event => this.loadGalleryMedia(event.detail)
    document.addEventListener('cartoonize:gallery-selected', this.gallerySelectionHandler)
    this.overlayPointerHandler = event => {
      const overlay = event.target.closest('.canvas-overlay')
      if (overlay) this.selectOverlay(overlay)
      else this.clearSelection()
    }
    this.overlayLayerTarget.addEventListener('pointerdown', this.overlayPointerHandler)
    this.syncCanvasBackground()
    this.emptyStateTarget.hidden = true
    this.ensureDrawingLayer(true)
    this.renderPages()
    this.syncPageControls()
    if (this.hasProjectDataTarget) this.loadSavedProject()
    globalThis.requestAnimationFrame(() => { this.baseCanvasWidth = this.canvasWrapTarget.offsetWidth })
  }

  disconnect () {
    document.removeEventListener('keydown', this.keydownHandler)
    document.removeEventListener('cartoonize:gallery-selected', this.gallerySelectionHandler)
    this.overlayLayerTarget.removeEventListener('pointerdown', this.overlayPointerHandler)
    this.pages.forEach(page => {
      if (page.videoObjectUrl) globalThis.URL.revokeObjectURL(page.videoObjectUrl)
    })
    clearTimeout(this.toastTimer)
  }

  selectTool (event) {
    const tool = event.currentTarget.dataset.tool
    this.element.querySelectorAll('.cartoonize-tool').forEach(button => {
      const active = button === event.currentTarget
      button.classList.toggle('active', active)
      button.setAttribute('aria-expanded', String(active))
    })
    this.paneTargets.forEach(pane => pane.classList.toggle('active', pane.dataset.pane === tool))
    this.setDrawingMode(tool === 'draw')
  }

  setDrawingMode (enabled = true) {
    this.drawingCanvasTarget.classList.toggle('drawing-enabled', enabled)
    this.canvasWrapTarget.classList.toggle('draw-mode', enabled)
    if (enabled) this.clearSelection()
    else this.isDrawing = false
  }

  openFile () { this.fileInputTarget.click() }

  defaultPage () {
    return { mediaType: 'image', image: null, source: null, imageFile: null, video: null, videoSource: null, videoFile: null, videoObjectUrl: null, filter: 'none', engineId: null, background: DEFAULT_CANVAS_BACKGROUND, template: null, templateId: null, templateUrl: null, imageTransform: null, design: '', overlays: '', drawingSnapshot: null }
  }

  loadFile (event) {
    const file = event.target.files[0]
    if (!file) return
    if (file.type.startsWith('video/')) {
      this.loadVideoFile(file)
      event.target.value = ''
      return
    }
    this.loadImageFile(file)
    event.target.value = ''
  }

  loadImageFile (file) {
    if (file.size > 15 * 1024 * 1024) return this.notify('Please choose an image under 15 MB.')
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/heic', 'image/heif'].includes(file.type)) return this.notify('Please choose a PNG, JPEG, WEBP, GIF, HEIC, or HEIF image.')
    const reader = new globalThis.FileReader()
    reader.onload = () => {
      const image = new globalThis.Image()
      image.onload = () => {
        this.remember()
        const page = this.pages[this.pageIndex]
        this.releasePageVideo(page)
        page.mediaType = 'image'
        page.image = image
        page.source = reader.result
        page.imageFile = file
        page.background = 'transparent'
        this.syncCanvasBackground()
        this.syncPageControls()
        this.fitCanvas(image, true)
        this.initializeImageTransform()
        this.redraw()
        this.createMainImageControl()
        this.emptyStateTarget.hidden = true
        this.renderPages()
        this.notify('Image added. Pick a cartoon engine to begin.')
      }
      image.src = reader.result
    }
    reader.readAsDataURL(file)
  }

  async loadGalleryMedia (media) {
    if (!media?.url || !media?.mediaType) return

    try {
      const mediaUrl = new globalThis.URL(media.url, globalThis.location.origin)
      if (mediaUrl.origin !== globalThis.location.origin || !mediaUrl.pathname.startsWith('/rails/active_storage/')) throw new Error('Invalid gallery media URL')

      this.notify('Loading gallery media…')
      const response = await globalThis.fetch(mediaUrl, { credentials: 'same-origin' })
      if (!response.ok) throw new Error('Gallery media could not be downloaded')

      const blob = await response.blob()
      const type = blob.type || media.contentType || ''
      const expectedPrefix = media.mediaType === 'video' ? 'video/' : 'image/'
      if (!type.startsWith(expectedPrefix)) throw new Error('Gallery media type is invalid')

      const filename = media.filename || `gallery-${Date.now()}`
      const file = new globalThis.File([blob], filename, { type })
      if (media.mediaType === 'video') this.loadVideoFile(file)
      else this.loadImageFile(file)
    } catch (error) {
      this.notify(error.message || 'Gallery media could not be loaded.')
    }
  }

  loadVideoFile (file) {
    if (file.size > 100 * 1024 * 1024) return this.notify('Please choose a video under 100 MB.')
    if (!['video/mp4', 'video/webm', 'video/quicktime'].includes(file.type)) return this.notify('Please choose an MP4, WEBM, or MOV video.')

    const objectUrl = globalThis.URL.createObjectURL(file)
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.playsInline = true
    video.onloadedmetadata = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0 || video.duration > 120) {
        globalThis.URL.revokeObjectURL(objectUrl)
        return this.notify('Video must be 2 minutes or shorter.')
      }
      this.remember()
      const page = this.pages[this.pageIndex]
      this.releasePageVideo(page)
      page.mediaType = 'video'
      page.image = null
      page.source = null
      page.video = video
      page.videoSource = objectUrl
      page.videoObjectUrl = objectUrl
      page.videoFile = file
      page.background = 'transparent'
      this.syncCanvasBackground()
      this.syncPageControls()
      this.fitCanvas(video, true)
      this.initializeImageTransform()
      this.redraw()
      this.createMainImageControl()
      this.emptyStateTarget.hidden = true
      this.renderPages()
      this.notify('Video added. Press Play/Pause, then add templates, text, stickers, or drawings.')
    }
    video.onerror = () => {
      globalThis.URL.revokeObjectURL(objectUrl)
      this.notify('This video could not be loaded.')
    }
    video.src = objectUrl
  }

  releasePageVideo (page) {
    page.video?.pause()
    if (page.videoObjectUrl) globalThis.URL.revokeObjectURL(page.videoObjectUrl)
    page.video = null
    page.videoSource = null
    page.videoFile = null
    page.videoObjectUrl = null
  }

  fitCanvas (image, resetDrawing = false) {
    const sourceWidth = image.videoWidth || image.width
    const sourceHeight = image.videoHeight || image.height
    if (!sourceWidth || !sourceHeight) return

    this.canvasTarget.width = DEFAULT_CANVAS_SIZE
    this.canvasTarget.height = DEFAULT_CANVAS_SIZE
    this.ensureDrawingLayer(resetDrawing)
  }

  applyEngine (event) {
    const engine = Number(event.currentTarget.dataset.engine)
    const page = this.pages[this.pageIndex]
    if (Number(page.engineId) === engine) return this.removeEngineSelection()

    this.remember()
    page.engineId = engine
    page.filter = ENGINE_FILTERS[engine - 1]
    this.engineNameTarget.textContent = `Cartoonizer ${engine}`
    this.engineSettingsTarget.classList.remove('d-none')
    this.syncPageControls()
    this.syncCanvasBackground()
    this.redraw()
  }

  removeEngineSelection () {
    const page = this.pages[this.pageIndex]
    if (!page?.engineId) return this.notify('No Cartoonizer is selected.')

    this.remember()
    page.engineId = null
    page.filter = 'none'
    this.engineSettingsTarget.classList.add('d-none')
    this.syncPageControls()
    this.syncCanvasBackground()
    this.redraw()
    this.renderPages()
    this.notify('Cartoonizer removed.')
    this.closeToolPanelOnCompactScreen()
  }

  updateEngine (event) {
    this.rememberInput(event)
    event.currentTarget.previousElementSibling.textContent = event.currentTarget.value
    const values = [...this.engineSettingsTarget.querySelectorAll('input')].map(input => Number(input.value))
    this.pages[this.pageIndex].filter = `contrast(${1 + values[1] / 100}) saturate(${0.7 + values[4] / 65}) brightness(${0.8 + values[3] / 250}) blur(${values[2] / 180}px)`
    this.syncCanvasBackground()
    this.redraw()
  }

  confirmSettings () {
    this.engineSettingsTarget.classList.add('d-none')
    this.notify('Cartoon effect applied.')
    this.closeToolPanelOnCompactScreen()
  }

  closeSettings () { this.engineSettingsTarget.classList.add('d-none') }

  applyFilter (event) {
    this.remember()
    const page = this.pages[this.pageIndex]
    page.engineId = null
    page.filter = IMAGE_FILTERS[Number(event.currentTarget.dataset.filter) % IMAGE_FILTERS.length]
    event.currentTarget.closest('.filter-grid')?.querySelectorAll('.filter-card').forEach(card => card.classList.toggle('selected', card === event.currentTarget))
    this.syncPageControls()
    this.syncCanvasBackground()
    this.redraw()
    this.closeToolPanelOnCompactScreen()
  }

  applyTemplate (event) {
    this.remember()
    const source = event.currentTarget.querySelector('img')
    const templateUrl = this.canvasSafeMediaUrl(source?.src)
    if (!templateUrl) return this.notify('This template could not be loaded securely.')
    const templateId = Number(event.currentTarget.dataset.templateId || 1)
    const template = new globalThis.Image()
    template.onload = () => {
      const page = this.pages[this.pageIndex]
      page.template = template
      page.templateId = templateId
      page.templateUrl = templateUrl
      this.canvasTarget.width = 900
      this.canvasTarget.height = 900
      this.ensureDrawingLayer(true)
      this.createTemplateFrameOverlay()
      if (this.hasPageMedia(page)) {
        this.fitImageToTemplateSlot()
        this.createMainImageControl()
      }
      this.emptyStateTarget.hidden = true
      this.syncPageControls()
      this.redraw()
      this.renderPages()
      this.notify('Template applied. Drag or resize the uploaded image inside its placeholder.')
      this.closeToolPanelOnCompactScreen()
    }
    template.src = templateUrl
  }

  removeTemplate () {
    const page = this.pages[this.pageIndex]
    if (!page.template && !page.templateUrl) return this.notify('No template is selected.')

    this.remember()
    page.template = null
    page.templateId = null
    page.templateUrl = null
    this.overlayLayerTarget.querySelectorAll('.template-frame-overlay').forEach(frame => frame.remove())
    this.syncPageControls()
    this.redraw()
    this.renderPages()
    this.notify('Template removed.')
    this.closeToolPanelOnCompactScreen()
  }

  updateCanvasBackground (event) {
    const page = this.pages[this.pageIndex]
    if (!page) return

    this.rememberInput(event)
    page.background = event.currentTarget.value
    this.syncCanvasBackground()
    this.redraw()
    this.renderPages()
  }

  resetCanvasBackground () {
    const page = this.pages[this.pageIndex]
    if (!page || page.background === DEFAULT_CANVAS_BACKGROUND) return

    this.remember()
    page.background = DEFAULT_CANVAS_BACKGROUND
    this.syncCanvasBackground()
    this.syncPageControls()
    this.redraw()
    this.renderPages()
    this.notify('Default canvas background restored.')
  }

  syncPageControls () {
    const page = this.pages[this.pageIndex]
    if (!page) return

    if (this.hasBackgroundInputTarget) {
      this.backgroundInputTarget.value = /^#[0-9a-f]{6}$/i.test(page.background || '') ? page.background : DEFAULT_CANVAS_BACKGROUND
    }
    if (this.hasRemoveTemplateButtonTarget) this.removeTemplateButtonTarget.disabled = !page.template && !page.templateUrl
    if (this.hasRemoveEngineButtonTarget) this.removeEngineButtonTarget.disabled = !page.engineId
    this.element.querySelectorAll('.engine-card').forEach(button => {
      const selected = Number(button.dataset.engine) === Number(page.engineId)
      button.classList.toggle('selected', selected)
      button.setAttribute('aria-pressed', String(selected))
    })
    this.element.querySelectorAll('.template-image').forEach(button => {
      button.classList.toggle('selected', Number(button.dataset.templateId) === Number(page.templateId))
    })
  }

  syncCanvasBackground () {
    const page = this.pages[this.pageIndex]
    if (!page || !this.hasBackgroundLayerTarget) return

    this.canvasWrapTarget.style.background = 'transparent'
    this.backgroundLayerTarget.style.backgroundColor = page.background || DEFAULT_CANVAS_BACKGROUND
    this.backgroundLayerTarget.style.filter = page.engineId ? page.filter || 'none' : 'none'
  }

  templateSlot (templateId = 1) {
    return TEMPLATE_SLOTS[templateId] || TEMPLATE_SLOTS[1]
  }

  fitImageToTemplateSlot (preserveTransform = true) {
    const page = this.pages[this.pageIndex]
    if (!this.hasPageMedia(page)) return
    const slot = this.templateSlot(page.templateId)
    this.fitMediaWithinBounds({
      x: slot.x / 100 * this.canvasTarget.width,
      y: slot.y / 100 * this.canvasTarget.height,
      width: slot.width / 100 * this.canvasTarget.width,
      height: slot.height / 100 * this.canvasTarget.height
    }, preserveTransform)
  }

  fitMediaWithinBounds (bounds, preserveTransform = true) {
    const page = this.pages[this.pageIndex]
    const media = page.mediaType === 'video' ? page.video : page.image
    const sourceWidth = media?.videoWidth || media?.naturalWidth || media?.width
    const sourceHeight = media?.videoHeight || media?.naturalHeight || media?.height
    const previous = preserveTransform ? page.imageTransform || {} : {}
    let width = bounds.width
    let height = bounds.height

    if (sourceWidth > 0 && sourceHeight > 0) {
      const scale = Math.min(bounds.width / sourceWidth, bounds.height / sourceHeight)
      width = sourceWidth * scale
      height = sourceHeight * scale
    }

    page.imageTransform = {
      x: bounds.x + (bounds.width - width) / 2,
      y: bounds.y + (bounds.height - height) / 2,
      width,
      height,
      rotation: previous.rotation || 0,
      flipX: previous.flipX || 1,
      flipY: previous.flipY || 1,
      opacity: previous.opacity || 1
    }
  }

  createTemplateFrameOverlay () {
    const page = this.pages[this.pageIndex]
    this.overlayLayerTarget.querySelectorAll('.template-frame-overlay').forEach(frame => frame.remove())
    const source = page.templateUrl || page.template?.src
    if (!source) return
    const frame = document.createElement('img')
    frame.className = 'template-frame-overlay'
    frame.src = source
    frame.alt = ''
    frame.setAttribute('aria-hidden', 'true')
    this.overlayLayerTarget.appendChild(frame)
  }

  applyDesign (event) {
    this.remember()
    const className = event.currentTarget.dataset.design || [...event.currentTarget.classList].find(name => name.startsWith('design-'))
    this.pages[this.pageIndex].design = className
    this.canvasWrapTarget.dataset.design = className
    event.currentTarget.closest('.asset-grid')?.querySelectorAll('.design-card').forEach(card => card.classList.toggle('selected', card === event.currentTarget))
    this.notify('Design applied.')
    this.closeToolPanelOnCompactScreen()
  }

  selectDesignGroup (event) {
    const group = event.currentTarget.dataset.designGroup
    const pane = event.currentTarget.closest('.cartoonize-pane')
    pane.querySelectorAll('.design-tabs button').forEach(button => button.classList.toggle('active', button === event.currentTarget))
    this.filterDesignCards(pane, group)
  }

  addBubble (event) {
    const source = event.currentTarget.querySelector('img')
    const bubbleSource = event.currentTarget.dataset.bubbleSrc || source.src
    const overlay = document.createElement('div')
    const text = document.createElement('span')
    overlay.className = 'canvas-overlay image-overlay bubble-overlay selected'
    overlay.appendChild(this.createOverlayImage(bubbleSource))
    text.className = 'bubble-text'
    text.textContent = 'Edit text'
    overlay.appendChild(text)
    this.addOverlay(overlay)
    this.editOverlay(overlay)
  }

  addSticker (event) {
    const overlay = document.createElement('div')
    overlay.className = 'canvas-overlay sticker-overlay selected'
    overlay.textContent = event.currentTarget.dataset.sticker
    overlay.dataset.rotation = -5
    this.applyObjectTransform(overlay)
    this.addOverlay(overlay)
  }

  addElement (event) {
    const element = event.currentTarget.dataset.element
    const overlay = document.createElement('div')
    overlay.className = `canvas-overlay shape-overlay ${element} selected`
    const [width, height] = ELEMENT_PRESETS[element] || ['22%', '18%']
    overlay.style.width = width
    overlay.style.height = height
    if (element?.startsWith('frame')) {
      overlay.style.left = '15%'
      overlay.style.top = '15%'
    }
    this.addOverlay(overlay)
  }

  addText (event) {
    const overlay = document.createElement('div')
    const style = event.currentTarget.dataset.textStyle || 'body'
    overlay.className = `canvas-overlay text-overlay ${style} selected`
    overlay.contentEditable = 'true'
    overlay.textContent = style === 'heading' ? 'Your heading' : style === 'subheading' ? 'Your subheading' : 'Edit this text'
    if (event.currentTarget.dataset.font) overlay.style.fontFamily = event.currentTarget.dataset.font
    this.addOverlay(overlay)
    overlay.focus()
  }

  addOverlay (overlay) {
    this.remember()
    this.emptyStateTarget.hidden = true
    overlay.style.left ||= '35%'
    overlay.style.top ||= '35%'
    this.prepareOverlay(overlay)
    this.overlayLayerTarget.appendChild(overlay)
    this.normalizeLayerOrder()
    this.selectOverlay(overlay)
    this.closeToolPanelOnCompactScreen()
  }

  createOverlayImage (source) {
    const image = document.createElement('img')
    const safeSource = this.canvasSafeMediaUrl(source)
    if (safeSource) image.src = safeSource
    image.alt = ''
    return image
  }

  prepareOverlay (overlay) {
    if (overlay.cartoonizePrepared) return
    overlay.cartoonizePrepared = true
    if (overlay.classList.contains('bubble-overlay')) this.ensureBubbleArtwork(overlay)
    if (this.isFrameElement(overlay) && !overlay.querySelector('.object-drag-surface')) {
      const surface = document.createElement('span')
      surface.className = 'object-drag-surface'
      overlay.prepend(surface)
    }
    if (!overlay.querySelector('.object-selection-frame')) {
      const frame = document.createElement('span')
      frame.className = 'object-selection-frame'
      overlay.appendChild(frame)
    }
    if (overlay.querySelectorAll('.object-resize-handle').length !== 8) {
      overlay.querySelectorAll('.object-resize-handle').forEach(handle => handle.remove())
      RESIZE_HANDLE_POSITIONS.forEach(position => {
        const handle = document.createElement('span')
        handle.className = `object-resize-handle object-resize-${position}`
        handle.dataset.resizeHandle = position
        handle.setAttribute('aria-label', `Resize object ${position}`)
        overlay.appendChild(handle)
      })
    }
    this.syncResizeHandleDirections(overlay)
    overlay.addEventListener('pointerdown', event => {
      event.stopPropagation()
      const handle = event.target.closest('.object-resize-handle')
      if (handle) return this.beginResize(event, overlay, handle.dataset.resizeHandle)
      this.beginDrag(event, overlay)
    })
    overlay.addEventListener('dblclick', () => this.editOverlay(overlay))
  }

  ensureBubbleArtwork (overlay) {
    if (overlay.querySelector('.bubble-artwork')) return
    const source = this.canvasSafeMediaUrl(overlay.querySelector('img')?.src)
    if (!source) return

    const artwork = document.createElement('span')
    artwork.className = 'bubble-artwork'
    artwork.style.maskImage = `url("${source}")`
    artwork.style.webkitMaskImage = `url("${source}")`
    overlay.insertBefore(artwork, overlay.querySelector('.bubble-text'))
  }

  hydrateOverlays () {
    this.overlayLayerTarget.querySelectorAll('.canvas-overlay').forEach(overlay => this.prepareOverlay(overlay))
    this.overlayLayerTarget.querySelectorAll('.selected').forEach(overlay => {
      overlay.classList.remove('selected')
      overlay.removeAttribute('aria-selected')
    })
    this.selectedOverlay = null
    this.clearSelection()
  }

  selectOverlay (overlay) {
    if (this.selectedOverlay === overlay && overlay.classList.contains('selected')) return
    if (this.selectedOverlay) {
      this.selectedOverlay.classList.remove('selected')
      this.selectedOverlay.removeAttribute('aria-selected')
    } else {
      this.overlayLayerTarget.querySelectorAll('.selected').forEach(item => {
        item.classList.remove('selected')
        item.removeAttribute('aria-selected')
      })
    }
    overlay.classList.add('selected')
    overlay.setAttribute('aria-selected', 'true')
    this.selectedOverlay = overlay
    this.objectLabelTarget.textContent = `${this.objectName(overlay)} selected`
    this.objectToolbarTarget.hidden = false
    if (this.hasMediaPlayButtonTarget) this.mediaPlayButtonTarget.hidden = !overlay.classList.contains('main-video-control')
    this.opacityInputTarget.value = Math.round(this.overlayOpacity(overlay) * 100)
    if (this.hasEditPanelTarget && !this.editPanelTarget.hidden) this.populateEditPanel(overlay)
    this.renderLayers()
  }

  clearSelection () {
    if (this.selectedOverlay) {
      this.selectedOverlay.classList.remove('selected')
      this.selectedOverlay.removeAttribute('aria-selected')
    }
    this.selectedOverlay = null
    this.objectToolbarTarget.hidden = true
    if (this.hasMediaPlayButtonTarget) this.mediaPlayButtonTarget.hidden = true
    if (this.hasEditPanelTarget) this.editPanelTarget.hidden = true
    this.renderLayers()
  }

  objectName (overlay) {
    if (overlay.classList.contains('main-video-control')) return 'Uploaded video'
    if (overlay.classList.contains('main-image-control')) return 'Uploaded image'
    if (overlay.classList.contains('text-overlay')) return 'Text'
    if (overlay.classList.contains('bubble-overlay')) return 'Comic bubble'
    if (overlay.classList.contains('image-overlay')) return 'Image'
    if (overlay.classList.contains('sticker-overlay')) return 'Sticker'
    return 'Element'
  }

  isLineElement (overlay) {
    return overlay.classList.contains('line') || overlay.classList.contains('arrow') || overlay.classList.contains('arrow-dot')
  }

  isFixedRatioShape (overlay) {
    return overlay.classList.contains('circle') || overlay.classList.contains('square') || overlay.classList.contains('triangle')
  }

  isFrameElement (overlay) {
    return overlay.classList.contains('shape-overlay') && [...overlay.classList].some(name => name.startsWith('frame'))
  }

  hasPageMedia (page = this.pages[this.pageIndex]) {
    return Boolean(page?.image || page?.video)
  }

  toggleVideoPlayback () {
    const video = this.selectedOverlay?.querySelector('video')
    if (!video) return
    if (video.paused) video.play().catch(() => this.notify('Video playback could not start.'))
    else video.pause()
  }

  beginDrag (event, overlay) {
    if (event.target.closest('.object-resize-handle')) return
    if (overlay.isContentEditable && document.activeElement === overlay) return
    event.preventDefault()
    this.selectOverlay(overlay)
    const rect = this.overlayLayerTarget.getBoundingClientRect()
    const startX = event.clientX
    const startY = event.clientY
    const originalLeft = overlay.offsetLeft
    const originalTop = overlay.offsetTop
    const overlayWidth = overlay.offsetWidth
    const overlayHeight = overlay.offsetHeight
    let nextLeft = originalLeft
    let nextTop = originalTop
    let frame = null
    let checkpointed = false
    const applyMove = () => {
      overlay.style.left = `${nextLeft}px`
      overlay.style.top = `${nextTop}px`
      frame = null
    }
    const move = e => {
      if (!checkpointed) {
        this.remember()
        checkpointed = true
      }
      const proposedLeft = originalLeft + e.clientX - startX
      const proposedTop = originalTop + e.clientY - startY
      if (overlay.classList.contains('main-image-control')) {
        const minimumVisible = Math.min(40, rect.width * 0.15, rect.height * 0.15)
        nextLeft = Math.max(-overlayWidth + minimumVisible, Math.min(rect.width - minimumVisible, proposedLeft))
        nextTop = Math.max(-overlayHeight + minimumVisible, Math.min(rect.height - minimumVisible, proposedTop))
      } else {
        nextLeft = Math.max(0, Math.min(rect.width - overlayWidth, proposedLeft))
        nextTop = Math.max(0, Math.min(rect.height - overlayHeight, proposedTop))
      }
      if (!frame) frame = globalThis.requestAnimationFrame(applyMove)
    }
    const end = () => {
      if (frame) {
        globalThis.cancelAnimationFrame(frame)
        applyMove()
      }
      if (overlay.classList.contains('main-image-control')) this.syncImageTransform(overlay)
      if (this.hasEditPanelTarget && !this.editPanelTarget.hidden) this.populateEditPanel(overlay)
      globalThis.removeEventListener('pointermove', move)
      globalThis.removeEventListener('pointerup', end)
      globalThis.removeEventListener('pointercancel', end)
    }
    globalThis.addEventListener('pointermove', move)
    globalThis.addEventListener('pointerup', end)
    globalThis.addEventListener('pointercancel', end)
  }

  beginResize (event, overlay, handle = 'se') {
    event.preventDefault()
    event.stopPropagation()
    this.selectOverlay(overlay)
    const resizeHandle = this.resizeHandleDirection(overlay, handle)
    const layerRect = this.overlayLayerTarget.getBoundingClientRect()
    const startX = event.clientX
    const startY = event.clientY
    const startLeft = overlay.offsetLeft
    const startTop = overlay.offsetTop
    const startWidth = overlay.offsetWidth
    const startHeight = overlay.offsetHeight
    const startFontSize = Number.parseFloat(globalThis.getComputedStyle(overlay).fontSize)
    const aspectRatio = startWidth / Math.max(startHeight, 1)
    const isLineShape = this.isLineElement(overlay)
    const isShapeWithFixedRatio = this.isFixedRatioShape(overlay)
    const isMainImage = overlay.classList.contains('main-image-control')
    const maximumWidth = isMainImage ? layerRect.width * 3 : layerRect.width
    const maximumHeight = isMainImage ? layerRect.height * 3 : layerRect.height
    const minWidth = isLineShape ? 70 : 45
    const minHeight = isLineShape ? 28 : 30
    const affectsLeft = resizeHandle.includes('w')
    const affectsRight = resizeHandle.includes('e')
    const affectsTop = resizeHandle.includes('n')
    const affectsBottom = resizeHandle.includes('s')
    let nextWidth = startWidth
    let nextHeight = startHeight
    let nextLeft = startLeft
    let nextTop = startTop
    let frame = null
    let checkpointed = false
    const applyResize = () => {
      overlay.style.left = `${nextLeft}px`
      overlay.style.top = `${nextTop}px`
      overlay.style.width = `${nextWidth}px`
      if (!overlay.classList.contains('text-overlay')) overlay.style.height = `${nextHeight}px`
      if (this.isTextEditable(overlay)) overlay.style.fontSize = `${Math.max(12, startFontSize * nextWidth / startWidth)}px`
      frame = null
    }
    const move = e => {
      if (!checkpointed) {
        this.remember()
        checkpointed = true
      }
      const deltaX = e.clientX - startX
      const deltaY = e.clientY - startY
      const isCorner = (affectsLeft || affectsRight) && (affectsTop || affectsBottom)

      if (isCorner && !e.altKey) {
        const signedDeltaX = affectsLeft ? -deltaX : deltaX
        const signedDeltaY = affectsTop ? -deltaY : deltaY
        const delta = Math.max(signedDeltaX, signedDeltaY * aspectRatio)
        nextWidth = Math.max(minWidth, Math.min(startWidth + delta, maximumWidth))
        nextHeight = Math.max(minHeight, Math.min(nextWidth / aspectRatio, maximumHeight))
      } else if (isShapeWithFixedRatio && !e.altKey) {
        const widthDelta = affectsRight ? deltaX : affectsLeft ? -deltaX : 0
        const heightDelta = affectsBottom ? deltaY : affectsTop ? -deltaY : 0
        const delta = Math.abs(widthDelta) > Math.abs(heightDelta * aspectRatio) ? widthDelta : heightDelta * aspectRatio
        nextWidth = Math.max(minWidth, startWidth + delta)
        nextHeight = Math.max(minHeight, nextWidth / aspectRatio)
      } else {
        nextWidth = Math.max(minWidth, startWidth + (affectsRight ? deltaX : affectsLeft ? -deltaX : 0))
        nextHeight = Math.max(minHeight, startHeight + (affectsBottom ? deltaY : affectsTop ? -deltaY : 0))
      }

      if (isLineShape && !e.altKey) nextHeight = startHeight
      nextWidth = Math.min(nextWidth, maximumWidth)
      nextHeight = Math.min(nextHeight, maximumHeight)

      if (affectsLeft) nextLeft = startLeft + startWidth - nextWidth
      if (affectsTop) nextTop = startTop + startHeight - nextHeight
      if (!affectsLeft) nextLeft = startLeft
      if (!affectsTop) nextTop = startTop

      if (!isMainImage && nextLeft < 0) {
        nextWidth += nextLeft
        nextLeft = 0
      }
      if (!isMainImage && nextTop < 0) {
        nextHeight += nextTop
        nextTop = 0
      }
      if (!isMainImage && nextLeft + nextWidth > layerRect.width) nextWidth = layerRect.width - nextLeft
      if (!isMainImage && nextTop + nextHeight > layerRect.height) nextHeight = layerRect.height - nextTop

      if (!frame) frame = globalThis.requestAnimationFrame(applyResize)
    }
    const end = () => {
      if (frame) {
        globalThis.cancelAnimationFrame(frame)
        applyResize()
      }
      if (overlay.classList.contains('main-image-control')) this.syncImageTransform(overlay)
      if (this.hasEditPanelTarget && !this.editPanelTarget.hidden) this.populateEditPanel(overlay)
      globalThis.removeEventListener('pointermove', move)
      globalThis.removeEventListener('pointerup', end)
      globalThis.removeEventListener('pointercancel', end)
    }
    globalThis.addEventListener('pointermove', move)
    globalThis.addEventListener('pointerup', end)
    globalThis.addEventListener('pointercancel', end)
  }

  resizeHandleDirection (overlay, handle) {
    const flipX = Number(overlay.dataset.flipX || 1)
    const flipY = Number(overlay.dataset.flipY || 1)
    return [...handle].map(direction => {
      if (flipX < 0 && direction === 'w') return 'e'
      if (flipX < 0 && direction === 'e') return 'w'
      if (flipY < 0 && direction === 'n') return 's'
      if (flipY < 0 && direction === 's') return 'n'
      return direction
    }).join('')
  }

  syncResizeHandleDirections (overlay) {
    const cursors = { nw: 'nwse-resize', n: 'ns-resize', ne: 'nesw-resize', e: 'ew-resize', se: 'nwse-resize', s: 'ns-resize', sw: 'nesw-resize', w: 'ew-resize' }
    overlay.querySelectorAll('.object-resize-handle').forEach(handle => {
      const direction = this.resizeHandleDirection(overlay, handle.dataset.resizeHandle)
      handle.style.cursor = cursors[direction]
      handle.setAttribute('aria-label', `Resize object ${direction}`)
    })
  }

  editSelected () {
    if (!this.selectedOverlay) return
    this.editOverlay(this.selectedOverlay)
  }

  editOverlay (overlay) {
    this.selectOverlay(overlay)
    this.beginObjectEditSession(overlay)
    this.populateEditPanel(overlay)
    this.editPanelTarget.hidden = false
    if (this.isTextEditable(overlay)) {
      if (!overlay.classList.contains('bubble-overlay')) overlay.contentEditable = 'true'
      this.editPanelTarget.querySelector('[data-edit-field="text"]')?.focus()
      this.notify('Edit options opened.')
    } else {
      this.notify('Edit options opened. Adjust size, color, border, or transform.')
    }
  }

  beginObjectEditSession (overlay) {
    if (this.editSession) this.discardObjectEdits(false)
    const page = this.pages[this.pageIndex]
    this.editSession = {
      overlay,
      historyLength: this.history.length,
      style: overlay.getAttribute('style'),
      rotation: overlay.dataset.rotation,
      flipX: overlay.dataset.flipX,
      flipY: overlay.dataset.flipY,
      text: this.isTextEditable(overlay) ? this.objectText(overlay) : null,
      imageTransform: page.imageTransform ? { ...page.imageTransform } : null
    }
    this.remember()
  }

  populateEditPanel (overlay) {
    if (!this.hasEditPanelTarget) return
    const panel = this.editPanelTarget
    panel.querySelector('[data-edit-label]').textContent = `Edit ${this.objectName(overlay)}`
    panel.querySelectorAll('[data-edit-group="text"]').forEach(group => { group.hidden = !this.isTextEditable(overlay) })
    const textInput = panel.querySelector('[data-edit-field="text"]')
    if (textInput) textInput.value = this.objectText(overlay)
    const computed = globalThis.getComputedStyle(overlay)
    const colorInput = panel.querySelector('[data-edit-field="color"]')
    const backgroundInput = panel.querySelector('[data-edit-field="background"]')
    const fontInput = panel.querySelector('[data-edit-field="fontSize"]')
    const weightInput = panel.querySelector('[data-edit-field="fontWeight"]')
    const borderInput = panel.querySelector('[data-edit-field="borderWidth"]')
    const paddingInput = panel.querySelector('[data-edit-field="padding"]')
    const widthInput = panel.querySelector('[data-edit-field="width"]')
    const heightInput = panel.querySelector('[data-edit-field="height"]')
    if (colorInput) colorInput.value = this.colorToHex(computed.color, '#111111')
    if (backgroundInput) backgroundInput.value = this.colorToHex(this.objectBackground(overlay, computed), '#ffcf2d')
    if (fontInput) fontInput.value = Math.round(Number.parseFloat(computed.fontSize) || 24)
    if (weightInput) weightInput.value = Math.round(Number.parseFloat(computed.fontWeight) || 400)
    if (borderInput) borderInput.value = Math.round(Number.parseFloat(computed.borderTopWidth) || 0)
    if (paddingInput) paddingInput.value = Math.round(Number.parseFloat(computed.paddingTop) || 0)
    if (widthInput) widthInput.value = Math.round(overlay.offsetWidth)
    if (heightInput) heightInput.value = Math.round(overlay.offsetHeight)
  }

  closeEditPanel (event) {
    event?.preventDefault()
    this.discardObjectEdits()
  }

  applyObjectEdits () {
    const invalidInput = [...this.editPanelTarget.querySelectorAll('input')].find(input => !input.checkValidity())
    if (invalidInput) {
      invalidInput.reportValidity()
      return
    }

    this.editSession = null
    this.editPanelTarget.hidden = true
    if (this.selectedOverlay?.isContentEditable) this.selectedOverlay.blur()
    this.notify('Object changes applied.')
  }

  discardObjectEdits (notify = true) {
    const session = this.editSession
    if (!session) {
      if (this.hasEditPanelTarget) this.editPanelTarget.hidden = true
      return
    }

    const { overlay } = session
    if (session.style === null) overlay.removeAttribute('style')
    else overlay.setAttribute('style', session.style)
    this.restoreDatasetValue(overlay, 'rotation', session.rotation)
    this.restoreDatasetValue(overlay, 'flipX', session.flipX)
    this.restoreDatasetValue(overlay, 'flipY', session.flipY)
    if (session.text !== null) this.setObjectText(overlay, session.text)

    if (overlay.classList.contains('main-image-control') && session.imageTransform) {
      this.pages[this.pageIndex].imageTransform = { ...session.imageTransform }
      this.createMainImageControl()
    } else this.syncResizeHandleDirections(overlay)

    this.history.splice(session.historyLength)
    this.editSession = null
    this.editPanelTarget.hidden = true
    if (overlay.isContentEditable) overlay.blur()
    this.opacityInputTarget.value = Math.round(this.overlayOpacity(overlay) * 100)
    this.redraw()
    if (notify) this.notify('Object changes discarded.')
  }

  restoreDatasetValue (overlay, key, value) {
    if (value === undefined) delete overlay.dataset[key]
    else overlay.dataset[key] = value
  }

  isTextEditable (overlay) {
    return overlay.classList.contains('text-overlay') || overlay.classList.contains('sticker-overlay') || overlay.classList.contains('bubble-overlay')
  }

  objectText (overlay) {
    if (overlay.classList.contains('bubble-overlay')) return overlay.querySelector('.bubble-text')?.textContent.trim() || ''
    return [...overlay.childNodes].filter(node => node.nodeType === globalThis.Node.TEXT_NODE).map(node => node.textContent).join('').trim()
  }

  setObjectText (overlay, value) {
    const text = value.trim() || 'Text'
    if (overlay.classList.contains('bubble-overlay')) {
      const textElement = overlay.querySelector('.bubble-text')
      if (textElement) textElement.textContent = text
      return
    }
    const textNode = [...overlay.childNodes].find(node => node.nodeType === globalThis.Node.TEXT_NODE)
    if (textNode) textNode.textContent = text
    else overlay.insertBefore(document.createTextNode(text), overlay.firstChild)
  }

  updateObjectText (event) {
    if (!this.selectedOverlay || !this.isTextEditable(this.selectedOverlay)) return
    this.rememberInput(event)
    this.setObjectText(this.selectedOverlay, event.currentTarget.value)
  }

  updateObjectColor (event) {
    if (!this.selectedOverlay) return
    this.rememberInput(event)
    this.selectedOverlay.style.color = event.currentTarget.value
    if (this.isLineElement(this.selectedOverlay)) {
      this.selectedOverlay.style.borderTopColor = event.currentTarget.value
      this.selectedOverlay.style.setProperty('--shape-color', event.currentTarget.value)
    }
  }

  updateObjectBackground (event) {
    if (!this.selectedOverlay || (this.selectedOverlay.classList.contains('image-overlay') && !this.selectedOverlay.classList.contains('bubble-overlay')) || this.selectedOverlay.classList.contains('main-image-control')) return
    this.rememberInput(event)
    const color = event.currentTarget.value
    if (this.selectedOverlay.classList.contains('bubble-overlay')) this.selectedOverlay.style.setProperty('--bubble-fill', color)
    else if (this.selectedOverlay.classList.contains('triangle')) this.selectedOverlay.style.borderBottomColor = color
    else if (this.isLineElement(this.selectedOverlay)) {
      this.selectedOverlay.style.borderTopColor = color
      this.selectedOverlay.style.setProperty('--shape-color', color)
    } else this.selectedOverlay.style.backgroundColor = color
  }

  updateObjectFontSize (event) {
    if (!this.selectedOverlay || !this.isTextEditable(this.selectedOverlay)) return
    const value = this.validatedEditValue(event.currentTarget)
    if (value === null) return
    this.rememberInput(event)
    this.selectedOverlay.style.fontSize = `${value}px`
  }

  updateObjectFontWeight (event) {
    if (!this.selectedOverlay || !this.isTextEditable(this.selectedOverlay)) return
    const value = this.validatedEditValue(event.currentTarget)
    if (value === null) return
    this.rememberInput(event)
    this.selectedOverlay.style.fontWeight = value
  }

  updateObjectBorderWidth (event) {
    if (!this.selectedOverlay || this.selectedOverlay.classList.contains('image-overlay') || this.selectedOverlay.classList.contains('main-image-control')) return
    const value = this.validatedEditValue(event.currentTarget)
    if (value === null) return
    this.rememberInput(event)
    const width = `${value}px`
    if (this.isLineElement(this.selectedOverlay)) {
      this.selectedOverlay.style.borderTopWidth = width
      this.selectedOverlay.style.setProperty('--shape-stroke', width)
    } else this.selectedOverlay.style.borderWidth = width
  }

  updateObjectPadding (event) {
    if (!this.selectedOverlay || this.selectedOverlay.classList.contains('main-image-control')) return
    const value = this.validatedEditValue(event.currentTarget)
    if (value === null) return
    this.rememberInput(event)
    this.selectedOverlay.style.padding = `${value}px`
  }

  updateObjectSize (event) {
    if (!this.selectedOverlay) return
    const value = this.validatedEditValue(event.currentTarget)
    if (value === null) return
    this.rememberInput(event)
    const field = event.currentTarget.dataset.editField
    this.selectedOverlay.style[field] = `${value}px`
    if (this.selectedOverlay.classList.contains('main-image-control')) this.syncImageTransform(this.selectedOverlay)
  }

  validatedEditValue (input) {
    input.setCustomValidity('')
    const value = input.valueAsNumber
    const min = Number(input.min)
    const max = Number(input.max)
    const valid = Number.isFinite(value) && !input.validity.rangeUnderflow && !input.validity.rangeOverflow && !input.validity.stepMismatch
    input.classList.toggle('is-invalid', !valid)
    if (valid) return value

    const label = input.dataset.editLabel || 'Value'
    input.setCustomValidity(`${label} must be a valid number from ${min} to ${max}${input.step && input.step !== 'any' ? ` in steps of ${input.step}` : ''}.`)
    return null
  }

  validateEditField (event) {
    if (this.validatedEditValue(event.currentTarget) === null) event.currentTarget.reportValidity()
  }

  resetObjectEdits () {
    if (!this.selectedOverlay) return
    if (!this.editSession) this.remember()
    const overlay = this.selectedOverlay

    if (overlay.classList.contains('main-image-control')) {
      this.initializeImageTransform()
      this.createMainImageControl()
    } else {
      overlay.style.left = this.isFrameElement(overlay) ? '15%' : '35%'
      overlay.style.top = this.isFrameElement(overlay) ? '15%' : '35%'
      overlay.style.removeProperty('width')
      overlay.style.removeProperty('height')
      if (overlay.classList.contains('sticker-overlay') || overlay.classList.contains('bubble-overlay')) {
        this.resetComicObjectAppearance(overlay)
      }
      const elementType = [...overlay.classList].find(name => ELEMENT_PRESETS[name])
      if (elementType) {
        const [width, height] = ELEMENT_PRESETS[elementType]
        overlay.style.width = width
        overlay.style.height = height
      }
    }

    overlay.dataset.rotation = overlay.classList.contains('sticker-overlay') ? -5 : 0
    overlay.dataset.flipX = 1
    overlay.dataset.flipY = 1
    overlay.style.opacity = 1
    this.opacityInputTarget.value = 100
    this.applyObjectTransform(overlay)
    if (overlay.classList.contains('main-image-control')) this.syncImageTransform(overlay)
    this.populateEditPanel(overlay)
    this.redraw()
    this.notify('Object transform reset.')
  }

  resetComicObjectAppearance (overlay) {
    const properties = ['color', 'background-color', 'font-size', 'font-family', 'font-weight', 'border-width', 'border-color', 'border-style', 'padding', 'text-shadow']
    properties.forEach(property => {
      overlay.style.removeProperty(property)
    })
    overlay.style.removeProperty('--bubble-fill')
  }

  objectBackground (overlay, computed) {
    if (overlay.classList.contains('bubble-overlay')) return computed.getPropertyValue('--bubble-fill').trim() || '#60e8c8'
    if (overlay.classList.contains('triangle')) return computed.borderBottomColor
    if (this.isLineElement(overlay)) return computed.borderTopColor
    return computed.backgroundColor
  }

  colorToHex (color, fallback) {
    if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return fallback
    if (color.startsWith('#')) return color
    const match = color.match(/\d+(\.\d+)?/g)
    if (!match || match.length < 3) return fallback
    return `#${match.slice(0, 3).map(value => Number(value).toString(16).padStart(2, '0')).join('')}`
  }

  deleteSelected () {
    if (!this.selectedOverlay) return
    this.remember()
    if (this.selectedOverlay.classList.contains('main-image-control')) {
      this.resetCurrentPage()
      this.clearSelection()
      this.renderLayers()
      this.renderPages()
      this.notify('Canvas cleared.')
      return
    }
    this.selectedOverlay.remove()
    this.clearSelection()
    this.notify('Object deleted.')
    this.renderLayers()
  }

  resetCurrentPage () {
    const page = this.pages[this.pageIndex]
    this.releasePageVideo(page)
    Object.assign(page, this.defaultPage())
    delete page.drawingCanvas
    this.canvasTarget.width = 900
    this.canvasTarget.height = 900
    this.ctx.clearRect(0, 0, this.canvasTarget.width, this.canvasTarget.height)
    this.drawingCanvasTarget.width = 900
    this.drawingCanvasTarget.height = 900
    this.visibleDrawingCtx = this.drawingCanvasTarget.getContext('2d')
    this.visibleDrawingCtx.clearRect(0, 0, this.drawingCanvasTarget.width, this.drawingCanvasTarget.height)
    this.overlayLayerTarget.innerHTML = ''
    this.syncCanvasBackground()
    this.canvasWrapTarget.dataset.design = ''
    this.emptyStateTarget.hidden = true
    this.syncPageControls()
    this.ensureDrawingLayer(true)
  }

  duplicateSelected () {
    if (!this.selectedOverlay) return
    this.remember()
    let duplicate
    if (this.selectedOverlay.classList.contains('main-image-control')) {
      const source = this.pages[this.pageIndex].source
      if (!source) return
      duplicate = document.createElement('div')
      duplicate.className = 'canvas-overlay image-overlay'
      duplicate.appendChild(this.createOverlayImage(source))
      duplicate.style.width = `${this.selectedOverlay.offsetWidth}px`
      duplicate.style.height = `${this.selectedOverlay.offsetHeight}px`
    } else {
      duplicate = this.selectedOverlay.cloneNode(true)
      duplicate.querySelectorAll('.object-selection-frame, .object-resize-handle').forEach(item => item.remove())
      duplicate.classList.remove('selected')
    }
    duplicate.style.left = `${this.selectedOverlay.offsetLeft + 18}px`
    duplicate.style.top = `${this.selectedOverlay.offsetTop + 18}px`
    this.prepareOverlay(duplicate)
    this.overlayLayerTarget.appendChild(duplicate)
    this.selectOverlay(duplicate)
    this.notify('Object duplicated.')
  }

  rotateLeft () { this.rotateSelected(-15) }
  rotateRight () { this.rotateSelected(15) }
  rotateSelected (amount) {
    if (!this.selectedOverlay) return
    this.remember()
    const rotation = Number(this.selectedOverlay.dataset.rotation || 0) + amount
    this.selectedOverlay.dataset.rotation = rotation
    this.applyObjectTransform(this.selectedOverlay)
  }

  flipHorizontal () { this.flipSelected('x') }
  flipVertical () { this.flipSelected('y') }
  flipSelected (axis) {
    if (!this.selectedOverlay) return
    this.remember()
    const key = axis === 'x' ? 'flipX' : 'flipY'
    this.selectedOverlay.dataset[key] = String(Number(this.selectedOverlay.dataset[key] || 1) * -1)
    this.applyObjectTransform(this.selectedOverlay)
  }

  applyObjectTransform (overlay) {
    const rotation = Number(overlay.dataset.rotation || 0)
    const flipX = Number(overlay.dataset.flipX || 1)
    const flipY = Number(overlay.dataset.flipY || 1)
    overlay.style.transform = `rotate(${rotation}deg) scale(${flipX}, ${flipY})`
    this.syncResizeHandleDirections(overlay)
    if (overlay.classList.contains('main-image-control')) {
      const transform = this.pages[this.pageIndex].imageTransform
      Object.assign(transform, { rotation, flipX, flipY })
    }
  }

  changeOpacity (event) {
    if (!this.selectedOverlay) return
    this.rememberInput(event)
    const opacity = Number(event.currentTarget.value) / 100
    this.selectedOverlay.style.opacity = opacity
    if (this.selectedOverlay.classList.contains('main-image-control')) {
      this.pages[this.pageIndex].imageTransform.opacity = opacity
    }
  }

  overlayOpacity (overlay) { return Number(overlay.style.opacity || 1) }

  bringForward () { this.moveLayer(1) }
  sendBackward () { this.moveLayer(-1) }
  moveLayer (direction) {
    const overlay = this.selectedOverlay
    if (!overlay) return
    this.remember()
    const layers = this.objectLayers()
    const index = layers.indexOf(overlay)
    if (index === -1) return
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= layers.length) return this.notify(direction > 0 ? 'Object is already at the front.' : 'Object is already behind other objects.')

    layers[index] = layers[targetIndex]
    layers[targetIndex] = overlay
    this.applyLayerOrder(layers)
    this.renderLayers()
    this.notify(direction > 0 ? 'Object brought forward.' : 'Object sent backward.')
  }

  objectLayers () {
    return [...this.overlayLayerTarget.querySelectorAll('.canvas-overlay')]
      .sort((a, b) => Number(a.style.zIndex || 0) - Number(b.style.zIndex || 0))
  }

  applyLayerOrder (layers = this.objectLayers()) {
    const fragment = document.createDocumentFragment()
    layers.forEach((layer, index) => {
      layer.style.zIndex = index + 2
      fragment.appendChild(layer)
    })
    this.overlayLayerTarget.appendChild(fragment)
  }

  normalizeLayerOrder () {
    this.applyLayerOrder([...this.overlayLayerTarget.querySelectorAll('.canvas-overlay')])
  }

  zoomIn () { this.setZoom(this.zoom + 0.1) }
  zoomOut () { this.setZoom(this.zoom - 0.1) }
  resetZoom () { this.setZoom(1) }
  setZoom (value) {
    this.zoom = Math.max(0.5, Math.min(2, Math.round(value * 10) / 10))
    this.canvasWrapTarget.style.width = `${(this.baseCanvasWidth || 600) * this.zoom}px`
    this.zoomLabelTarget.textContent = `${Math.round(this.zoom * 100)}%`
  }

  toggleLayers () {
    this.layersPanelTarget.hidden = !this.layersPanelTarget.hidden
    if (!this.layersPanelTarget.hidden) this.renderLayers(true)
  }

  renderLayers (force = false) {
    if (!this.hasLayersListTarget) return
    if (!force && this.layersPanelTarget.hidden) return
    this.layersListTarget.innerHTML = ''
    const overlays = this.objectLayers().reverse()
    const fragment = document.createDocumentFragment()
    overlays.forEach((overlay, index) => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = overlay === this.selectedOverlay ? 'active' : ''
      const iconType = overlay.classList.contains('text-overlay') ? 'text' : overlay.classList.contains('main-image-control') || overlay.classList.contains('image-overlay') ? 'image' : 'element'
      const icon = this.element.querySelector(`[data-layer-icon="${iconType}"]`)?.innerHTML || ''
      button.innerHTML = `${icon}<span>${this.objectName(overlay)}</span><small>${overlays.length - index}</small>`
      button.addEventListener('click', () => this.selectOverlay(overlay))
      fragment.appendChild(button)
    })
    this.layersListTarget.appendChild(fragment)
  }

  handleKeydown (event) {
    if (!this.selectedOverlay) return
    const target = event.target
    if (target.matches('input, textarea, [contenteditable="true"]')) return
    if (['Delete', 'Backspace'].includes(event.key)) { event.preventDefault(); this.deleteSelected(); return }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd') { event.preventDefault(); this.duplicateSelected(); return }
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      event.preventDefault()
      this.remember()
      const step = event.shiftKey ? 10 : 1
      const x = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0
      const y = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0
      this.selectedOverlay.style.left = `${this.selectedOverlay.offsetLeft + x}px`
      this.selectedOverlay.style.top = `${this.selectedOverlay.offsetTop + y}px`
      if (this.selectedOverlay.classList.contains('main-image-control')) this.syncImageTransform(this.selectedOverlay)
    }
  }

  chooseBrush (event) {
    this.brushSize = Number(event.currentTarget.dataset.size)
    this.drawCompositeOperation = 'source-over'
    this.selectDrawOption(event.currentTarget, 'brush')
    this.setDrawingMode(true)
  }

  chooseColor (event) {
    this.brushColor = event.currentTarget.dataset.color
    this.drawCompositeOperation = 'source-over'
    this.selectDrawOption(event.currentTarget, 'color')
    this.setDrawingMode(true)
  }

  chooseEraser (event) {
    this.brushSize = Number(event.currentTarget.value)
    this.brushColor = '#000'
    this.drawCompositeOperation = 'destination-out'
    this.selectDrawOption(event.currentTarget, 'eraser')
    this.setDrawingMode(true)
  }

  selectDrawOption (element, group) {
    const pane = element.closest('[data-pane="draw"]')
    if (!pane) return
    if (group === 'brush') pane.querySelectorAll('[data-draw-option="brush"]').forEach(option => option.classList.toggle('selected', option === element))
    if (group === 'color') pane.querySelectorAll('[data-color]').forEach(option => option.classList.toggle('selected', option === element))
    pane.querySelectorAll('[data-draw-option="eraser"]').forEach(option => option.classList.toggle('selected', group === 'eraser'))
  }

  ensureDrawingLayer (reset = false) {
    this.syncCanvasAspectRatio()
    const page = this.pages[this.pageIndex]
    if (!page) return
    const needsCanvas = !page.drawingCanvas || reset || page.drawingCanvas.width !== this.canvasTarget.width || page.drawingCanvas.height !== this.canvasTarget.height
    if (needsCanvas) {
      const previous = page.drawingCanvas
      const drawingCanvas = document.createElement('canvas')
      drawingCanvas.width = this.canvasTarget.width
      drawingCanvas.height = this.canvasTarget.height
      if (previous && !reset) drawingCanvas.getContext('2d').drawImage(previous, 0, 0, drawingCanvas.width, drawingCanvas.height)
      page.drawingCanvas = drawingCanvas
      page.drawingSnapshot = null
    }
    this.drawingCtx = page.drawingCanvas.getContext('2d')
    if (this.drawingCanvasTarget.width !== this.canvasTarget.width || this.drawingCanvasTarget.height !== this.canvasTarget.height) {
      this.drawingCanvasTarget.width = this.canvasTarget.width
      this.drawingCanvasTarget.height = this.canvasTarget.height
      this.visibleDrawingCtx = this.drawingCanvasTarget.getContext('2d')
    }
    this.visibleDrawingCtx.clearRect(0, 0, this.drawingCanvasTarget.width, this.drawingCanvasTarget.height)
    this.visibleDrawingCtx.drawImage(page.drawingCanvas, 0, 0)
  }

  syncCanvasAspectRatio () {
    const width = Math.max(1, this.canvasTarget.width)
    const height = Math.max(1, this.canvasTarget.height)
    const ratio = `${width} / ${height}`
    if (this.canvasAspectRatio === ratio) return
    this.canvasAspectRatio = ratio
    this.canvasWrapTarget.style.setProperty('--canvas-aspect-ratio', ratio)
  }

  configureDrawingContext (context) {
    context.globalCompositeOperation = this.drawCompositeOperation
    context.lineWidth = this.brushSize
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.strokeStyle = this.brushColor
  }

  startDraw (event) {
    if (!this.drawingCanvasTarget.classList.contains('drawing-enabled')) return
    event.preventDefault()
    this.drawingCanvasTarget.setPointerCapture?.(event.pointerId)
    this.remember()
    this.ensureDrawingLayer()
    this.isDrawing = true
    this.configureDrawingContext(this.visibleDrawingCtx)
    this.configureDrawingContext(this.drawingCtx)
    this.visibleDrawingCtx.beginPath()
    this.drawingCtx.beginPath()
    const point = this.canvasPoint(event)
    this.visibleDrawingCtx.moveTo(point.x, point.y)
    this.drawingCtx.moveTo(point.x, point.y)
  }

  draw (event) {
    if (!this.isDrawing) return
    event.preventDefault()
    const point = this.canvasPoint(event)
    this.configureDrawingContext(this.visibleDrawingCtx)
    this.configureDrawingContext(this.drawingCtx)
    this.visibleDrawingCtx.lineTo(point.x, point.y)
    this.drawingCtx.lineTo(point.x, point.y)
    this.visibleDrawingCtx.stroke()
    this.drawingCtx.stroke()
  }

  endDraw (event) {
    if (this.isDrawing) this.pages[this.pageIndex].drawingSnapshot = null
    this.isDrawing = false
    if (event?.pointerId !== undefined) this.drawingCanvasTarget.releasePointerCapture?.(event.pointerId)
  }

  canvasPoint (event) { const rect = this.drawingCanvasTarget.getBoundingClientRect(); return { x: (event.clientX - rect.left) * this.canvasTarget.width / rect.width, y: (event.clientY - rect.top) * this.canvasTarget.height / rect.height } }

  clearDrawing () {
    this.remember()
    this.ensureDrawingLayer()
    this.drawingCtx.clearRect(0, 0, this.canvasTarget.width, this.canvasTarget.height)
    this.pages[this.pageIndex].drawingSnapshot = null
    this.visibleDrawingCtx.clearRect(0, 0, this.drawingCanvasTarget.width, this.drawingCanvasTarget.height)
    this.redraw()
  }

  addPage () {
    if (this.pages.length >= MAX_PAGES) return this.notify(`You can add up to ${MAX_PAGES} pages.`)

    this.pages.push(this.defaultPage())
    this.switchPage(this.pages.length - 1)
  }

  deletePage () {
    if (this.pages.length <= 1) return this.notify('At least one page is required.')

    const deletedPageNumber = this.pageIndex + 1
    const deletedPage = this.pages[this.pageIndex]
    this.clearSelection()
    this.releasePageVideo(deletedPage)
    this.pages.splice(this.pageIndex, 1)

    const nextPageIndex = Math.min(this.pageIndex, this.pages.length - 1)
    this.pageIndex = -1
    this.switchPage(nextPageIndex)
    this.notify(`Page ${deletedPageNumber} deleted.`)
  }

  switchPage (index) {
    const currentPage = this.pages[this.pageIndex]
    if (currentPage) currentPage.overlays = this.serializeOverlays()
    this.pageIndex = index
    const page = this.pages[index]
    this.emptyStateTarget.hidden = true
    if (page.template) {
      this.canvasTarget.width = 900
      this.canvasTarget.height = 900
      this.ensureDrawingLayer()
    } else if (this.hasPageMedia(page)) {
      this.fitCanvas(page.video || page.image)
    } else {
      this.ensureDrawingLayer()
    }
    this.overlayLayerTarget.innerHTML = page.overlays || ''
    this.hydrateOverlays()
    if (page.template) this.createTemplateFrameOverlay()
    if (this.hasPageMedia(page)) this.createMainImageControl()
    this.syncCanvasBackground()
    this.canvasWrapTarget.dataset.design = page.design || ''
    this.syncPageControls()
    this.redraw()
    this.renderPages()
  }

  initializeImageTransform () {
    const page = this.pages[this.pageIndex]
    if (!this.hasPageMedia(page)) return
    if (page.template) {
      this.fitImageToTemplateSlot(false)
    } else {
      this.fitMediaWithinBounds({ x: 0, y: 0, width: this.canvasTarget.width, height: this.canvasTarget.height }, false)
    }
  }

  createMainImageControl () {
    const page = this.pages[this.pageIndex]
    if (!this.hasPageMedia(page) || !page.imageTransform) return
    let control = this.overlayLayerTarget.querySelector('.main-image-control')
    if (!control) {
      control = document.createElement('div')
      control.className = `canvas-overlay main-image-control${page.mediaType === 'video' ? ' main-video-control' : ''}`
      control.dataset.mainImage = 'true'
      control.appendChild(page.mediaType === 'video' ? this.createOverlayVideo(page.videoSource) : this.createOverlayImage(page.source))
      this.prepareOverlay(control)
      this.overlayLayerTarget.prepend(control)
      this.normalizeLayerOrder()
    } else if (page.mediaType === 'video' && !control.querySelector('video')) {
      control.querySelector('img')?.remove()
      control.classList.add('main-video-control')
      control.prepend(this.createOverlayVideo(page.videoSource))
    } else if (page.mediaType !== 'video' && !control.querySelector('img')) {
      control.querySelector('video')?.remove()
      control.classList.remove('main-video-control')
      control.prepend(this.createOverlayImage(page.source))
    }
    const image = control.querySelector('img')
    if (image) {
      if (image.getAttribute('src') !== page.source) image.src = page.source
      image.style.filter = page.filter || 'none'
      image.style.objectFit = 'fill'
    }
    const video = control.querySelector('video')
    if (video) {
      if (video.getAttribute('src') !== page.videoSource) video.src = page.videoSource
      video.style.filter = page.filter || 'none'
      video.style.objectFit = 'fill'
      page.video = video
    }
    const transform = page.imageTransform
    control.style.left = `${transform.x / this.canvasTarget.width * 100}%`
    control.style.top = `${transform.y / this.canvasTarget.height * 100}%`
    control.style.width = `${transform.width / this.canvasTarget.width * 100}%`
    control.style.height = `${transform.height / this.canvasTarget.height * 100}%`
    control.dataset.rotation = transform.rotation || 0
    control.dataset.flipX = transform.flipX || 1
    control.dataset.flipY = transform.flipY || 1
    control.style.opacity = transform.opacity || 1
    control.style.transform = `rotate(${transform.rotation || 0}deg) scale(${transform.flipX || 1}, ${transform.flipY || 1})`
    this.syncResizeHandleDirections(control)
    this.selectOverlay(control)
  }

  syncImageTransform (control) {
    const layerRect = this.overlayLayerTarget.getBoundingClientRect()
    if (!layerRect.width || !layerRect.height) return
    this.pages[this.pageIndex].imageTransform = {
      x: control.offsetLeft / layerRect.width * this.canvasTarget.width,
      y: control.offsetTop / layerRect.height * this.canvasTarget.height,
      width: control.offsetWidth / layerRect.width * this.canvasTarget.width,
      height: control.offsetHeight / layerRect.height * this.canvasTarget.height,
      rotation: Number(control.dataset.rotation || 0),
      flipX: Number(control.dataset.flipX || 1),
      flipY: Number(control.dataset.flipY || 1),
      opacity: Number(control.style.opacity || 1)
    }
  }

  updateMainImageVisual () {
    const page = this.pages[this.pageIndex]
    const control = this.overlayLayerTarget.querySelector('.main-image-control')
    const image = control?.querySelector('img')
    const video = control?.querySelector('video')
    if (image) {
      if (image.getAttribute('src') !== page.source) image.src = page.source
      image.style.filter = page.filter || 'none'
      image.style.objectFit = 'fill'
    }
    if (video) {
      video.style.filter = page.filter || 'none'
      video.style.objectFit = 'fill'
    }
  }

  createOverlayVideo (source) {
    const video = document.createElement('video')
    const safeSource = this.canvasSafeMediaUrl(source)
    if (safeSource) video.src = safeSource
    video.preload = 'metadata'
    video.playsInline = true
    video.loop = true
    return video
  }

  renderPages () {
    this.pageStripTarget.innerHTML = ''
    this.pages.forEach((page, index) => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = index === this.pageIndex ? 'active' : ''
      button.textContent = `${index + 1}`
      button.title = `Page ${index + 1}`
      button.addEventListener('click', () => this.switchPage(index))
      this.pageStripTarget.appendChild(button)
    })

    if (this.hasAddPageButtonTarget) {
      const atPageLimit = this.pages.length >= MAX_PAGES
      this.addPageButtonTarget.disabled = atPageLimit
      this.addPageButtonTarget.setAttribute('aria-disabled', atPageLimit)
      this.addPageButtonTarget.title = atPageLimit ? `Maximum ${MAX_PAGES} pages reached` : `Add page (${this.pages.length}/${MAX_PAGES})`
    }
    if (this.hasDeletePageButtonTarget) {
      const onlyPage = this.pages.length <= 1
      this.deletePageButtonTarget.disabled = onlyPage
      this.deletePageButtonTarget.setAttribute('aria-disabled', onlyPage)
      this.deletePageButtonTarget.title = onlyPage ? 'At least one page is required' : `Delete page ${this.pageIndex + 1}`
    }
  }

  serializeOverlays () {
    return [...this.overlayLayerTarget.querySelectorAll('.canvas-overlay')].map(overlay => overlay.outerHTML).join('')
  }

  serializeProjectOverlays () {
    const styleProperties = ['left', 'top', 'width', 'height', 'color', 'backgroundColor', 'borderWidth', 'borderColor', 'borderTopWidth', 'borderTopColor', 'fontSize', 'fontFamily', 'fontWeight', 'padding', 'opacity', 'zIndex', 'transform']
    return [...this.overlayLayerTarget.querySelectorAll('.canvas-overlay:not(.main-image-control)')].map(overlay => {
      const style = {}
      styleProperties.forEach(property => {
        if (overlay.style[property]) style[property] = overlay.style[property]
      })
      const shapeColor = overlay.style.getPropertyValue('--shape-color')
      const shapeStroke = overlay.style.getPropertyValue('--shape-stroke')
      const bubbleFill = overlay.style.getPropertyValue('--bubble-fill')
      if (shapeColor) style['--shape-color'] = shapeColor
      if (shapeStroke) style['--shape-stroke'] = shapeStroke
      if (bubbleFill) style['--bubble-fill'] = bubbleFill
      return {
        classes: [...overlay.classList].filter(name => name !== 'selected'),
        style,
        rotation: Number(overlay.dataset.rotation || 0),
        flipX: Number(overlay.dataset.flipX || 1),
        flipY: Number(overlay.dataset.flipY || 1),
        text: this.isTextEditable(overlay) ? this.objectText(overlay) : null,
        asset: overlay.querySelector('img')?.src || null
      }
    })
  }

  buildEditorData () {
    const page = this.pages[this.pageIndex]
    return {
      version: 1,
      page_index: this.pageIndex,
      canvas: { width: this.canvasTarget.width, height: this.canvasTarget.height },
      media_type: page.mediaType,
      filter: page.filter || 'none',
      engine_id: page.engineId,
      background: page.background || DEFAULT_CANVAS_BACKGROUND,
      template_id: page.templateId,
      template_url: page.templateUrl,
      image_transform: page.imageTransform ? { ...page.imageTransform } : null,
      design: page.design || '',
      overlays: this.serializeProjectOverlays(),
      drawing: this.drawingDataUrl(page)
    }
  }

  drawingDataUrl (page = this.pages[this.pageIndex]) {
    if (!page?.drawingCanvas) return null
    page.drawingSnapshot ||= page.drawingCanvas.toDataURL('image/png')
    return page.drawingSnapshot
  }

  async loadSavedProject () {
    try {
      const payload = JSON.parse(this.projectDataTarget.textContent)
      const data = payload.editor_data || {}
      const page = this.defaultPage()
      this.projectId = payload.id
      this.projectName = payload.name
      page.mediaType = payload.media_type === 'video' ? 'video' : 'image'
      page.filter = data.filter || 'none'
      page.engineId = Number(data.engine_id) || null
      page.background = data.background || DEFAULT_CANVAS_BACKGROUND
      page.templateId = data.template_id || null
      page.templateUrl = this.safeProjectAssetUrl(data.template_url)
      page.imageTransform = data.image_transform || null
      page.design = data.design || ''
      this.pages = [page]
      this.pageIndex = 0

      if (page.mediaType === 'video') {
        page.videoSource = payload.media_url
        page.video = await this.loadEditorVideo(payload.media_url)
      } else {
        page.source = payload.media_url
        page.image = await this.loadEditorImage(payload.media_url)
      }
      page.template = await this.loadEditorImage(page.templateUrl)
      this.canvasTarget.width = Number(data.canvas?.width) || 900
      this.canvasTarget.height = Number(data.canvas?.height) || 900
      if (!page.imageTransform) this.initializeImageTransform()
      this.ensureDrawingLayer(true)
      if (data.drawing) {
        const drawing = await this.loadEditorImage(data.drawing)
        if (drawing) page.drawingCanvas.getContext('2d').drawImage(drawing, 0, 0, page.drawingCanvas.width, page.drawingCanvas.height)
        page.drawingSnapshot = data.drawing
      }
      this.overlayLayerTarget.innerHTML = ''
      this.restoreProjectOverlays(data.overlays)
      if (page.template) this.createTemplateFrameOverlay()
      if (this.hasPageMedia(page)) this.createMainImageControl()
      this.syncCanvasBackground()
      this.canvasWrapTarget.dataset.design = page.design
      this.emptyStateTarget.hidden = true
      this.syncPageControls()
      this.redraw()
      this.renderPages()
      this.renderLayers()
      this.notify('Saved project opened for editing.')
    } catch (error) {
      this.notify(`Project could not be opened: ${error.message}`)
    }
  }

  restoreProjectOverlays (overlays) {
    const allowedClasses = new Set(['canvas-overlay', 'image-overlay', 'bubble-overlay', 'sticker-overlay', 'text-overlay', 'shape-overlay', 'body', 'heading', 'subheading', 'line', 'arrow', 'arrow-dot', 'circle', 'square', 'triangle', 'frame', 'frame-cyan', 'frame-pink', 'frame-yellow'])
    const allowedStyles = new Set(['left', 'top', 'width', 'height', 'color', 'backgroundColor', 'borderWidth', 'borderColor', 'borderTopWidth', 'borderTopColor', 'fontSize', 'fontFamily', 'fontWeight', 'padding', 'opacity', 'zIndex', 'transform', '--shape-color', '--shape-stroke', '--bubble-fill'])
    if (!Array.isArray(overlays)) return
    overlays.slice(0, 100).forEach(item => {
      const overlay = document.createElement('div')
      const classes = Array.isArray(item.classes) ? item.classes.filter(name => allowedClasses.has(name)) : []
      overlay.className = [...new Set(['canvas-overlay', ...classes])].join(' ')
      Object.entries(item.style || {}).forEach(([property, value]) => {
        if (!allowedStyles.has(property) || typeof value !== 'string' || value.length > 100) return
        if (property.startsWith('--')) overlay.style.setProperty(property, value)
        else overlay.style[property] = value
      })
      overlay.dataset.rotation = Number(item.rotation || 0)
      overlay.dataset.flipX = Number(item.flipX || 1)
      overlay.dataset.flipY = Number(item.flipY || 1)
      const asset = this.safeProjectAssetUrl(item.asset)
      if (asset) overlay.appendChild(this.createOverlayImage(asset))
      if (overlay.classList.contains('bubble-overlay')) {
        const text = document.createElement('span')
        text.className = 'bubble-text'
        text.textContent = String(item.text || 'Text').slice(0, 500)
        overlay.appendChild(text)
      } else if (this.isTextEditable(overlay)) {
        overlay.appendChild(document.createTextNode(String(item.text || 'Text').slice(0, 500)))
      }
      this.prepareOverlay(overlay)
      this.overlayLayerTarget.appendChild(overlay)
    })
    this.normalizeLayerOrder()
  }

  safeProjectAssetUrl (source) {
    const safeSource = this.canvasSafeMediaUrl(source)
    if (!safeSource || safeSource.startsWith('data:') || safeSource.startsWith('blob:')) return null
    return safeSource
  }

  canvasSafeMediaUrl (source) {
    if (!source) return null
    try {
      const url = new URL(source, globalThis.location.origin)
      if (url.protocol === 'data:') {
        return /^data:image\/(?:png|jpeg|webp|gif|heic|heif);base64,/i.test(source) ? source : null
      }
      if (url.protocol === 'blob:') return url.origin === globalThis.location.origin ? source : null
      if (url.pathname.startsWith('/assets/')) return `${globalThis.location.origin}${url.pathname}${url.search}`
      if (url.origin === globalThis.location.origin && url.pathname.startsWith('/rails/active_storage/')) return url.href
      return null
    } catch (_) {
      return null
    }
  }

  loadEditorVideo (source) {
    if (!source) return Promise.resolve(null)
    return new Promise(resolve => {
      const video = this.createOverlayVideo(source)
      video.onloadedmetadata = () => resolve(video)
      video.onerror = () => resolve(null)
    })
  }

  redraw () {
    this.ensureDrawingLayer()
    this.ctx.clearRect(0, 0, this.canvasTarget.width, this.canvasTarget.height)
    this.updateMainImageVisual()
  }

  rememberInput (event) {
    const input = event.currentTarget
    if (this.editSession && input.closest('[data-cartoonize-target~="editPanel"]')) return
    if (input.dataset.undoCaptured === 'true') return
    this.remember()
    input.dataset.undoCaptured = 'true'
    const release = () => { delete input.dataset.undoCaptured }
    input.addEventListener('change', release, { once: true })
    input.addEventListener('blur', release, { once: true })
  }

  remember () {
    const page = this.pages[this.pageIndex]
    if (!page) return
    try {
      const snapshot = {
        pageIndex: this.pageIndex,
        canvasWidth: this.canvasTarget.width,
        canvasHeight: this.canvasTarget.height,
        mediaType: page.mediaType,
        source: page.source,
        videoSource: page.videoSource,
        videoFile: page.videoFile,
        filter: page.filter,
        engineId: page.engineId,
        background: page.background,
        templateId: page.templateId,
        templateUrl: page.templateUrl,
        imageTransform: page.imageTransform ? { ...page.imageTransform } : null,
        design: page.design,
        overlays: this.serializeOverlays(),
        drawing: this.drawingDataUrl(page)
      }
      this.history.push(snapshot)
      if (this.history.length > 20) this.history.shift()
    } catch (_) {}
  }

  async undo () {
    const snapshot = this.history.pop()
    if (!snapshot) return this.notify('Nothing to undo yet.')

    this.pageIndex = snapshot.pageIndex
    const page = this.pages[this.pageIndex] || this.defaultPage()
    this.pages[this.pageIndex] = page
    Object.assign(page, this.defaultPage(), {
      source: snapshot.source,
      mediaType: snapshot.mediaType || 'image',
      videoSource: snapshot.videoSource,
      videoFile: snapshot.videoFile,
      filter: snapshot.filter || 'none',
      engineId: snapshot.engineId || null,
      background: snapshot.background || DEFAULT_CANVAS_BACKGROUND,
      templateId: snapshot.templateId,
      templateUrl: snapshot.templateUrl,
      imageTransform: snapshot.imageTransform ? { ...snapshot.imageTransform } : null,
      design: snapshot.design || '',
      overlays: snapshot.overlays || ''
    })

    const [image, video, template] = await Promise.all([
      this.loadEditorImage(snapshot.mediaType === 'video' ? null : snapshot.source),
      this.loadEditorVideo(snapshot.mediaType === 'video' ? snapshot.videoSource : null),
      this.loadEditorImage(snapshot.templateUrl)
    ])
    page.image = image
    page.video = video
    page.template = template
    this.canvasTarget.width = snapshot.canvasWidth || 900
    this.canvasTarget.height = snapshot.canvasHeight || 900
    this.ensureDrawingLayer(true)
    if (snapshot.drawing) {
      const drawing = await this.loadEditorImage(snapshot.drawing)
      if (drawing) page.drawingCanvas.getContext('2d').drawImage(drawing, 0, 0, page.drawingCanvas.width, page.drawingCanvas.height)
      page.drawingSnapshot = snapshot.drawing
    }

    this.overlayLayerTarget.innerHTML = snapshot.overlays || ''
    this.hydrateOverlays()
    if (page.template) this.createTemplateFrameOverlay()
    if (this.hasPageMedia(page)) this.createMainImageControl()
    this.syncCanvasBackground()
    this.canvasWrapTarget.dataset.design = page.design
    this.emptyStateTarget.hidden = true
    this.syncPageControls()
    this.redraw()
    this.renderPages()
    this.renderLayers()
    this.notify('Last change undone.')
  }

  loadEditorImage (source) {
    if (!source) return Promise.resolve(null)
    const safeSource = this.canvasSafeMediaUrl(source)
    if (!safeSource) return Promise.resolve(null)
    return new Promise(resolve => {
      const image = new globalThis.Image()
      image.onload = () => resolve(image)
      image.onerror = () => resolve(null)
      image.src = safeSource
    })
  }

  async saveImage () {
    const page = this.pages[this.pageIndex]
    if (!this.hasPageMedia(page) && !page.template) return this.notify('Add an image, video, or template before saving.')

    this.saveButtonTarget.disabled = true
    this.saveButtonTarget.classList.add('saving')
    this.element.classList.add('is-saving')
    this.notify(page.mediaType === 'video' ? 'Uploading video…' : 'Saving image…')

    try {
      const formData = new globalThis.FormData()
      const name = this.projectName || `Cartoon ${new Date().toLocaleString()}`
      formData.append('cartoonized_image[name]', name)
      formData.append('cartoonized_image[editor_data]', JSON.stringify(this.buildEditorData()))

      if (page.mediaType === 'video') {
        this.notify('Rendering video with effects…')
        const renderedVideo = await this.renderedVideoBlob()
        if (renderedVideo.size > 100 * 1024 * 1024) throw new Error('Rendered video is larger than 100 MB. Shorten the video or reduce the canvas size.')
        const extension = renderedVideo.type.includes('mp4') ? 'mp4' : 'webm'
        formData.append('cartoonized_image[rendered_video]', renderedVideo, `itoons-cartoon-${Date.now()}.${extension}`)
        this.notify('Uploading rendered video…')
      }

      if (!this.projectId) {
        formData.append('cartoonized_image[media_type]', page.mediaType)
        if (page.mediaType === 'video') {
          if (!page.videoFile) throw new Error('Choose the original video again before saving this new project.')
          formData.append('cartoonized_image[video]', page.videoFile, page.videoFile.name)
        } else {
          const blob = await this.compositeBlob()
          formData.append('cartoonized_image[image]', page.imageFile || blob, page.imageFile?.name || `itoons-source-${Date.now()}.png`)
          formData.append('cartoonized_image[preview]', blob, `itoons-cartoon-${Date.now()}.png`)
        }
      }

      const response = await globalThis.fetch(this.projectId ? `/cartoonize/projects/${this.projectId}` : '/cartoonize', {
        method: this.projectId ? 'PATCH' : 'POST',
        headers: { Accept: 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '' },
        body: formData
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(result.errors?.join(', ') || `Unable to save ${page.mediaType}.`)

      this.projectId = result.id
      this.projectName = name
      this.notify(page.mediaType === 'video' ? 'Edited video saved with all effects.' : 'Image saved to your account.')
    } catch (error) {
      this.notify(error.message)
    } finally {
      this.saveButtonTarget.disabled = false
      this.saveButtonTarget.classList.remove('saving')
      this.element.classList.remove('is-saving')
    }
  }

  async download () {
    if (!this.hasPageMedia() && !this.pages[this.pageIndex].template) return this.notify('Add an image, video, or template before downloading.')
    const link = document.createElement('a')
    const isVideo = this.pages[this.pageIndex].mediaType === 'video'
    this.notify(isVideo ? 'Rendering video for download…' : 'Preparing image…')
    const blob = isVideo ? await this.renderedVideoBlob() : await this.compositeBlob()
    link.download = `itoons-cartoon-${Date.now()}.${isVideo ? (blob.type.includes('mp4') ? 'mp4' : 'webm') : 'png'}`
    link.href = globalThis.URL.createObjectURL(blob)
    link.click()
    globalThis.setTimeout(() => globalThis.URL.revokeObjectURL(link.href), 5000)
    this.notify(isVideo ? 'Edited video downloaded.' : 'Image downloaded.')
  }

  async compositeBlob () {
    await this.prepareCompositeAssets()
    const output = document.createElement('canvas')
    output.width = this.canvasTarget.width
    output.height = this.canvasTarget.height
    const context = output.getContext('2d')
    this.renderCompositeFrame(context, output)

    return await new Promise((resolve, reject) => output.toBlob(blob => blob ? resolve(blob) : reject(new Error('Unable to render image.')), 'image/png', 1))
  }

  async prepareCompositeAssets () {
    const images = [...this.overlayLayerTarget.querySelectorAll('.canvas-overlay img')]
    await Promise.all(images.map(image => image.decode().catch(() => {})))
    await document.fonts?.ready
  }

  renderCompositeFrame (context, output) {
    const entries = this.compositeOverlayEntries(output)
    this.drawCompositeBase(context, output)
    entries.forEach(entry => this.drawCompositeOverlay(context, entry))
    this.drawDrawingLayer(context, output.width, output.height)
    this.drawDesignFrame(context, output.width, output.height, entries.scaleX)
  }

  drawCompositeBase (context, output) {
    context.clearRect(0, 0, output.width, output.height)
    context.drawImage(this.renderedBackground(output.width, output.height), 0, 0)
    context.drawImage(this.canvasTarget, 0, 0)
    this.drawTemplateFrame(context, output.width, output.height)
  }

  renderedBackground (width, height) {
    const page = this.pages[this.pageIndex]
    const filter = page.engineId ? page.filter || 'none' : 'none'
    const key = `${width}:${height}:${page.background}:${filter}`
    if (this.backgroundRenderCache?.key === key) return this.backgroundRenderCache.canvas

    const source = document.createElement('canvas')
    source.width = width
    source.height = height
    const sourceContext = source.getContext('2d')
    sourceContext.fillStyle = page.background || DEFAULT_CANVAS_BACKGROUND
    sourceContext.fillRect(0, 0, width, height)

    if (filter === 'none') {
      this.backgroundRenderCache = { key, canvas: source }
      return source
    }

    const rendered = document.createElement('canvas')
    rendered.width = width
    rendered.height = height
    const renderedContext = rendered.getContext('2d')
    renderedContext.filter = filter
    renderedContext.drawImage(source, 0, 0)
    renderedContext.filter = 'none'
    this.backgroundRenderCache = { key, canvas: rendered }
    return rendered
  }

  compositeOverlayEntries (output) {
    const layerRect = this.overlayLayerTarget.getBoundingClientRect()
    const scaleX = output.width / layerRect.width
    const scaleY = output.height / layerRect.height
    const overlays = [...this.overlayLayerTarget.querySelectorAll('.canvas-overlay')]
      .sort((a, b) => Number(a.style.zIndex || 0) - Number(b.style.zIndex || 0))
    const entries = overlays.map(overlay => {
      const asset = overlay.querySelector('img, video')
      return {
        overlay,
        asset,
        x: overlay.offsetLeft * scaleX,
        y: overlay.offsetTop * scaleY,
        width: overlay.offsetWidth * scaleX,
        height: overlay.offsetHeight * scaleY,
        rotation: Number(overlay.dataset.rotation || 0) * Math.PI / 180,
        flipX: Number(overlay.dataset.flipX || 1),
        flipY: Number(overlay.dataset.flipY || 1),
        opacity: this.overlayOpacity(overlay),
        filter: asset?.style.filter || 'none',
        objectFit: asset ? globalThis.getComputedStyle(asset).objectFit : 'fill',
        scaleX,
        scaleY
      }
    })
    entries.scaleX = scaleX
    entries.scaleY = scaleY
    return entries
  }

  drawCompositeOverlay (context, entry) {
    const { overlay, asset, x, y, width, height, rotation, flipX, flipY, opacity, filter, objectFit, scaleX, scaleY } = entry
    context.save()
    context.globalAlpha = opacity
    context.translate(x + width / 2, y + height / 2)
    context.rotate(rotation)
    context.scale(flipX, flipY)
    const drawX = -width / 2
    const drawY = -height / 2

    if (asset) {
      context.filter = filter
      if (overlay.classList.contains('bubble-overlay')) {
        const fill = globalThis.getComputedStyle(overlay).getPropertyValue('--bubble-fill').trim() || '#60e8c8'
        this.drawTintedBubble(context, asset, drawX, drawY, width, height, fill)
      } else this.drawFittedImage(context, asset, drawX, drawY, width, height, objectFit)
      context.filter = 'none'
      if (overlay.classList.contains('bubble-overlay')) this.drawOverlayText(context, overlay, width, height, scaleY)
    } else if (overlay.classList.contains('text-overlay') || overlay.classList.contains('sticker-overlay')) {
      const style = globalThis.getComputedStyle(overlay)
      if (overlay.classList.contains('sticker-overlay')) this.drawStickerShape(context, style, drawX, drawY, width, height, scaleX)
      this.drawOverlayText(context, overlay, width, height, scaleY)
    } else {
      this.drawShape(context, overlay, drawX, drawY, width, height, scaleX)
    }
    context.restore()
  }

  drawTintedBubble (context, asset, x, y, width, height, color) {
    const renderWidth = Math.max(1, Math.ceil(width))
    const renderHeight = Math.max(1, Math.ceil(height))
    const source = asset.currentSrc || asset.src || ''
    const key = `${source}:${color}:${renderWidth}:${renderHeight}`
    this.bubbleTintCache ||= new Map()
    let tinted = this.bubbleTintCache.get(key)

    if (!tinted) {
      tinted = document.createElement('canvas')
      tinted.width = renderWidth
      tinted.height = renderHeight
      const tintedContext = tinted.getContext('2d')
      this.drawFittedImage(tintedContext, asset, 0, 0, renderWidth, renderHeight, 'fill')
      tintedContext.globalCompositeOperation = 'source-in'
      tintedContext.fillStyle = color
      tintedContext.fillRect(0, 0, renderWidth, renderHeight)
      tintedContext.globalCompositeOperation = 'source-over'
      this.bubbleTintCache.set(key, tinted)
      if (this.bubbleTintCache.size > 40) this.bubbleTintCache.delete(this.bubbleTintCache.keys().next().value)
    }

    context.drawImage(tinted, x, y, width, height)
  }

  buildVideoRenderScene (output) {
    const entries = this.compositeOverlayEntries(output)
    const videoIndex = entries.findIndex(entry => entry.overlay.classList.contains('main-video-control'))
    if (videoIndex < 0) throw new Error('The video layer is missing.')
    const background = document.createElement('canvas')
    const foreground = document.createElement('canvas')
    background.width = foreground.width = output.width
    background.height = foreground.height = output.height
    const backgroundContext = background.getContext('2d', { alpha: false })
    const foregroundContext = foreground.getContext('2d')
    this.drawCompositeBase(backgroundContext, background)
    entries.slice(0, videoIndex).forEach(entry => this.drawCompositeOverlay(backgroundContext, entry))
    entries.slice(videoIndex + 1).forEach(entry => this.drawCompositeOverlay(foregroundContext, entry))
    this.drawDrawingLayer(foregroundContext, output.width, output.height)
    this.drawDesignFrame(foregroundContext, output.width, output.height, entries.scaleX)
    return { background, foreground, video: entries[videoIndex] }
  }

  renderVideoFrame (context, output, scene) {
    context.clearRect(0, 0, output.width, output.height)
    context.drawImage(scene.background, 0, 0)
    this.drawCompositeOverlay(context, scene.video)
    context.drawImage(scene.foreground, 0, 0)
  }

  async renderedVideoBlob () {
    if (!globalThis.MediaRecorder || !globalThis.HTMLCanvasElement.prototype.captureStream) throw new Error('Video rendering is not supported in this browser. Please use the latest Chrome, Edge, or Firefox.')
    const video = this.overlayLayerTarget.querySelector('.main-video-control video')
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) throw new Error('The video is not ready to render.')

    await this.prepareCompositeAssets()
    const output = document.createElement('canvas')
    output.width = this.canvasTarget.width
    output.height = this.canvasTarget.height
    const context = output.getContext('2d', { alpha: false })
    const scene = this.buildVideoRenderScene(output)
    const canvasStream = output.captureStream(30)
    const capturedMedia = video.captureStream?.() || video.mozCaptureStream?.()
    const stream = new globalThis.MediaStream([
      ...canvasStream.getVideoTracks(),
      ...(capturedMedia?.getAudioTracks() || [])
    ])
    const mimeType = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4'
    ].find(type => globalThis.MediaRecorder.isTypeSupported(type))
    if (!mimeType) throw new Error('This browser cannot encode an edited video.')

    const recorder = new globalThis.MediaRecorder(stream, { mimeType, videoBitsPerSecond: 4_000_000 })
    const chunks = []
    const previous = { currentTime: video.currentTime, loop: video.loop, muted: video.muted }
    let animationFrame
    let renderTimeout
    let progressSecond = -1
    let renderError
    const stopped = new Promise((resolve, reject) => {
      recorder.ondataavailable = event => { if (event.data.size) chunks.push(event.data) }
      recorder.onerror = event => reject(event.error || new Error('Video encoding failed.'))
      recorder.onstop = resolve
    })

    try {
      video.pause()
      video.loop = false
      video.muted = true
      await this.seekVideo(video, 0)
      this.renderVideoFrame(context, output, scene)
      recorder.start(1000)
      renderTimeout = globalThis.setTimeout(() => {
        renderError = new Error('Video rendering timed out.')
        if (recorder.state !== 'inactive') recorder.stop()
      }, Math.ceil(video.duration * 1000) + 10_000)

      const render = () => {
        try {
          this.renderVideoFrame(context, output, scene)
          const second = Math.floor(video.currentTime)
          if (second !== progressSecond) {
            progressSecond = second
            const percent = Math.min(99, Math.round(video.currentTime / video.duration * 100))
            this.notify(`Rendering video… ${percent}%`)
          }
          if (!video.ended) animationFrame = globalThis.requestAnimationFrame(render)
        } catch (error) {
          renderError = error
          if (recorder.state !== 'inactive') recorder.stop()
        }
      }
      animationFrame = globalThis.requestAnimationFrame(render)
      video.addEventListener('ended', () => {
        this.renderVideoFrame(context, output, scene)
        if (recorder.state !== 'inactive') recorder.stop()
      }, { once: true })
      await video.play()
      await stopped
      if (renderError) throw renderError
      if (!chunks.length) throw new Error('The browser did not produce a rendered video.')
      return new Blob(chunks, { type: recorder.mimeType || mimeType })
    } finally {
      if (animationFrame) globalThis.cancelAnimationFrame(animationFrame)
      if (renderTimeout) globalThis.clearTimeout(renderTimeout)
      if (recorder.state !== 'inactive') recorder.stop()
      stream.getTracks().forEach(track => track.stop())
      video.pause()
      video.loop = previous.loop
      video.muted = previous.muted
      await this.seekVideo(video, Math.min(previous.currentTime, video.duration || 0)).catch(() => {})
    }
  }

  seekVideo (video, time) {
    if (Math.abs(video.currentTime - time) < 0.02) return Promise.resolve()
    return new Promise((resolve, reject) => {
      const timeout = globalThis.setTimeout(() => reject(new Error('Video seeking timed out.')), 5000)
      video.addEventListener('seeked', () => { globalThis.clearTimeout(timeout); resolve() }, { once: true })
      video.currentTime = time
    })
  }

  drawOverlayText (context, overlay, width, height, scale) {
    const textElement = overlay.querySelector('.bubble-text') || overlay
    const style = globalThis.getComputedStyle(textElement)
    const horizontalPadding = overlay.classList.contains('bubble-overlay') ? width * 0.18 : 8
    const maxWidth = Math.max(20, width - horizontalPadding * 2)
    const fontSize = Number.parseFloat(style.fontSize) * scale
    const lines = this.wrapCanvasText(context, this.objectText(overlay), maxWidth, style, fontSize)
    const lineHeight = fontSize * 1.12
    const startY = -((lines.length - 1) * lineHeight) / 2
    context.font = `${style.fontWeight} ${fontSize}px ${style.fontFamily}`
    context.fillStyle = style.color
    context.textBaseline = 'middle'
    context.textAlign = 'center'
    lines.forEach((line, index) => context.fillText(line, 0, startY + index * lineHeight, maxWidth))
  }

  wrapCanvasText (context, text, maxWidth, style, fontSize) {
    context.font = `${style.fontWeight} ${fontSize}px ${style.fontFamily}`
    const words = (text || 'Text').trim().split(/\s+/)
    const lines = []
    let line = ''
    words.forEach(word => {
      const candidate = line ? `${line} ${word}` : word
      if (line && context.measureText(candidate).width > maxWidth) {
        lines.push(line)
        line = word
      } else line = candidate
    })
    if (line) lines.push(line)
    return lines.slice(0, 4)
  }

  drawDrawingLayer (context, width = this.canvasTarget.width, height = this.canvasTarget.height) {
    const drawingCanvas = this.pages[this.pageIndex].drawingCanvas
    if (drawingCanvas) context.drawImage(drawingCanvas, 0, 0, width, height)
  }

  drawStickerShape (context, style, x, y, width, height, scale) {
    context.beginPath()
    STICKER_POINTS.forEach(([px, py], index) => {
      const pointX = x + width * px / 100
      const pointY = y + height * py / 100
      if (index === 0) context.moveTo(pointX, pointY)
      else context.lineTo(pointX, pointY)
    })
    context.closePath()
    context.fillStyle = style.backgroundColor || '#ffe12f'
    context.strokeStyle = style.borderTopColor || '#111111'
    context.lineWidth = Math.max(3, Number.parseFloat(style.borderTopWidth) * scale || 5 * scale)
    context.fill()
    context.stroke()
    context.clip()
  }

  drawDesignFrame (context, width = this.canvasTarget.width, height = this.canvasTarget.height, scale = 1) {
    const design = this.pages[this.pageIndex].design
    if (!design) return
    const style = DESIGN_FRAME_STYLES[design]
    if (!style) return
    context.save()
    context.strokeStyle = style.color
    const frameWidth = style.width * scale
    context.lineWidth = frameWidth
    context.setLineDash((style.dash || []).map(value => value * scale))
    context.strokeRect(frameWidth / 2, frameWidth / 2, width - frameWidth, height - frameWidth)
    if (style.double) {
      context.lineWidth = Math.max(6 * scale, frameWidth / 3)
      context.setLineDash([])
      const inset = frameWidth * 1.25
      context.strokeRect(inset, inset, width - inset * 2, height - inset * 2)
    }
    context.restore()
  }

  drawTemplateFrame (context, width = this.canvasTarget.width, height = this.canvasTarget.height) {
    const page = this.pages[this.pageIndex]
    if (!page.template) return
    context.save()
    context.drawImage(page.template, 0, 0, width, height)
    context.restore()
  }

  drawFittedImage (context, asset, x, y, width, height, fit = 'fill') {
    const imageWidth = asset.videoWidth || asset.naturalWidth || asset.width
    const imageHeight = asset.videoHeight || asset.naturalHeight || asset.height
    if (!imageWidth || !imageHeight || fit === 'fill') return context.drawImage(asset, x, y, width, height)

    const imageRatio = imageWidth / imageHeight
    const boxRatio = width / height

    if (fit === 'cover') {
      const sourceWidth = imageRatio > boxRatio ? imageHeight * boxRatio : imageWidth
      const sourceHeight = imageRatio > boxRatio ? imageHeight : imageWidth / boxRatio
      const sourceX = (imageWidth - sourceWidth) / 2
      const sourceY = (imageHeight - sourceHeight) / 2
      context.drawImage(asset, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height)
    } else {
      const drawWidth = imageRatio > boxRatio ? width : height * imageRatio
      const drawHeight = imageRatio > boxRatio ? width / imageRatio : height
      context.drawImage(asset, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight)
    }
  }

  drawShape (context, overlay, x, y, width, height, scale) {
    const computed = globalThis.getComputedStyle(overlay)
    const shapeColor = computed.getPropertyValue('--shape-color').trim()
    const shapeStroke = Number.parseFloat(computed.getPropertyValue('--shape-stroke'))
    const fillColor = computed.backgroundColor && computed.backgroundColor !== 'rgba(0, 0, 0, 0)' ? computed.backgroundColor : shapeColor || '#111111'
    const strokeColor = shapeColor || (computed.borderTopColor && computed.borderTopColor !== 'rgba(0, 0, 0, 0)' ? computed.borderTopColor : fillColor)
    const lineWidth = Math.max(3 * scale, shapeStroke * scale || Number.parseFloat(computed.borderTopWidth) || 7 * scale)

    context.save()
    context.fillStyle = fillColor
    context.strokeStyle = strokeColor
    context.lineWidth = lineWidth
    context.lineCap = 'round'
    context.lineJoin = 'round'

    if (overlay.classList.contains('circle')) {
      context.beginPath()
      context.ellipse(x + width / 2, y + height / 2, width / 2, height / 2, 0, 0, Math.PI * 2)
      context.fill()
    } else if (overlay.classList.contains('square')) {
      context.fillRect(x, y, width, height)
    } else if (overlay.classList.contains('triangle')) {
      context.beginPath()
      context.moveTo(x + width / 2, y)
      context.lineTo(x + width, y + height)
      context.lineTo(x, y + height)
      context.closePath()
      context.fill()
    } else if ([...overlay.classList].some(name => name.startsWith('frame'))) {
      const inset = context.lineWidth / 2
      context.strokeRect(x + inset, y + inset, Math.max(0, width - context.lineWidth), Math.max(0, height - context.lineWidth))
    } else {
      const centerY = y + height / 2
      context.beginPath()
      context.moveTo(x + context.lineWidth / 2, centerY)
      context.lineTo(x + width - context.lineWidth / 2, centerY)
      context.stroke()
      if (overlay.classList.contains('arrow') || overlay.classList.contains('arrow-dot')) {
        const headLength = Math.min(Math.max(lineWidth * 3.2, height * 0.48), width * 0.24, 34 * scale)
        const headHalf = Math.min(Math.max(lineWidth * 1.35, headLength * 0.38), height * 0.42)
        const neckInset = Math.max(lineWidth * 0.9, headLength * 0.26)
        context.beginPath()
        context.moveTo(x + width - context.lineWidth / 2, centerY)
        context.lineTo(x + width - headLength, centerY - headHalf)
        context.lineTo(x + width - headLength + neckInset, centerY)
        context.lineTo(x + width - headLength, centerY + headHalf)
        context.closePath()
        context.fillStyle = strokeColor
        context.fill()
      }
      if (overlay.classList.contains('arrow-dot')) {
        context.beginPath()
        context.arc(x + context.lineWidth / 2, centerY, Math.max(context.lineWidth * 0.85, 5 * scale), 0, Math.PI * 2)
        context.fillStyle = strokeColor
        context.fill()
      }
    }
    context.restore()
  }

  searchAssets (event) {
    const query = event.currentTarget.value.toLowerCase().trim()
    const pane = event.currentTarget.closest('.cartoonize-pane')
    if (pane.dataset.pane === 'designs') {
      const group = pane.querySelector('.design-tabs .active')?.dataset.designGroup
      this.filterDesignCards(pane, group, query)
      return
    }
    pane.querySelectorAll('[data-search]').forEach(item => { item.hidden = !item.dataset.search.toLowerCase().includes(query) })
  }

  filterDesignCards (pane, group, query = pane.querySelector('.panel-search')?.value.toLowerCase().trim() || '') {
    pane.querySelectorAll('.design-card').forEach(card => {
      const matchesGroup = !group || card.dataset.designGroup === group
      const matchesSearch = !query || card.dataset.search.toLowerCase().includes(query)
      card.hidden = !(matchesGroup && matchesSearch)
    })
  }

  notify (message) {
    this.toastTarget.textContent = message
    this.toastTarget.classList.add('show')
    clearTimeout(this.toastTimer)
    this.toastTimer = setTimeout(() => this.toastTarget.classList.remove('show'), 2800)
  }

  closeToolPanelOnCompactScreen () {
    if (!globalThis.matchMedia('(max-width: 767px)').matches) return
    this.closeToolPanel()
  }

  closeToolPanel () {
    this.element.querySelectorAll('.cartoonize-tool').forEach(tool => {
      tool.classList.remove('active')
      tool.setAttribute('aria-expanded', 'false')
    })
    this.paneTargets.forEach(pane => pane.classList.remove('active'))
  }

  closeModal (event) {
    event?.preventDefault()
    this.closeToolPanel()
  }
}
